import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'motion/react';
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  UsersIcon,
  MicrophoneIcon,
  FunnelIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  EyeIcon,
  CalendarIcon,
  EnvelopeIcon,
  CodeBracketIcon,
  DocumentMagnifyingGlassIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import useCandidateScoreModals from '../../hooks/useCandidateScoreModals';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import DateRangePopover from '../../components/ui/DateRangePopover';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../lib/axiosConfig';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

/* `/api/auth/account-overview` is fetched once on mount and is the ONLY source for the
   organisation's name, the Joined chip and `reportWorkbookAvailable` — the flag that decides
   whether the Excel download exists at all. Four attempts over ~6s covers a server restart or
   a network change; beyond that the server is genuinely unreachable and the page is empty
   regardless. See the effect that uses this. */
const ACCOUNT_OVERVIEW_RETRIES = 4;

const transactionIcons = {
  video_interview: { icon: VideoCameraIcon, bg: 'bg-[#F0EEFF]', color: 'text-[#5C53A3]' },
  audio_interview: { icon: MicrophoneIcon, bg: 'bg-[#F0EEFF]', color: 'text-[#5C53A3]' },
  assessment: { icon: AcademicCapIcon, bg: 'bg-[#E6F7ED]', color: 'text-[#10b981]' },
  resume_score: { icon: DocumentTextIcon, bg: 'bg-[#FFEFE6]', color: 'text-[#f97316]' },
  ocr_call: { icon: FunnelIcon, bg: 'bg-[#E8F4FF]', color: 'text-[#3b82f6]' },
};

const formatEvalDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const Profile = () => {
  const { user, updateProfile, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const {
    openInterviewSessionForCandidate,
    handleInlineViewAssessmentScore,
    openCandidateResumeBreakdown,
    ScoreModals,
  } = useCandidateScoreModals();
  const logoInputRef = useRef(null);
  const rangePickerRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const canEditProfile = user?.role !== 'user';
  const isOrgAdmin = user?.accountType === 'org' && user?.role === 'admin';

  const [activeTab, setActiveTab] = useState('account');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Email integration states
  const [emailStatus, setEmailStatus] = useState({
    loading: true,
    connected: false,
    email: '',
    displayName: '',
    status: '',
    lastError: '',
    lastUsedAt: '',
  });
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [isSavingDisplayName, setIsSavingDisplayName] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  const [accountForm, setAccountForm] = useState({
    name: '',
    company: '',
    position: '',
  });
  const [accountEditing, setAccountEditing] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [notifyOnApply, setNotifyOnApply] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);

  const [autoRetryFailedInterviews, setAutoRetryFailedInterviews] = useState(false);
  const [autoRetrySaving, setAutoRetrySaving] = useState(false);

  const [armNotificationsEnabled, setArmNotificationsEnabled] = useState(true);
  const [armNotificationsSaving, setArmNotificationsSaving] = useState(false);

  const [ageGatingEnabled, setAgeGatingEnabled] = useState(false);
  const [ageGatingRules, setAgeGatingRules] = useState([]);
  const [ageGatingSaving, setAgeGatingSaving] = useState(false);

  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  // globalRange is the selected preset: '1' | '7' | '30' | '90' | 'billing' |
  // 'all' | 'custom'. When it is 'custom', customRange carries the picked days.
  const [globalRange, setGlobalRange] = useState('all');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [hasSetDefaultRange, setHasSetDefaultRange] = useState(false);
  const [heatmapData, setHeatmapData] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(true);
  const [heatmapTotalActivity, setHeatmapTotalActivity] = useState(0);
  const [peakInterviewHour, setPeakInterviewHour] = useState('—');
  const [afterOfficeHoursPercent, setAfterOfficeHoursPercent] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [chartGranularity, setChartGranularity] = useState('day');
  const [chartLoading, setChartLoading] = useState(true);
  const [chartSeriesVisible, setChartSeriesVisible] = useState({
    interviews: true,
    jobPosts: true,
    screenings: true,
    assessments: true,
  });
  const [selectedCard, setSelectedCard] = useState(null);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionDays, setTransactionDays] = useState('all');
  const [evaluationsList, setEvaluationsList] = useState([]);
  const [evaluationsListLoading, setEvaluationsListLoading] = useState(false);
  const [evaluationsFilter, setEvaluationsFilter] = useState('all');

  const [sourcingList, setSourcingList] = useState([]);
  const [sourcingTotal, setSourcingTotal] = useState(0);
  const [sourcingBreakdown, setSourcingBreakdown] = useState([]);
  const [sourcingListLoading, setSourcingListLoading] = useState(false);

  const [pipelineLists, setPipelineLists] = useState({
    all: [],
    shortlisted: [],
    rejected: [],
  });
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineActiveTab, setPipelineActiveTab] = useState('all');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const [accountOverview, setAccountOverview] = useState(null);
  const [accountOverviewLoading, setAccountOverviewLoading] = useState(false);
  const [orgLogoUploading, setOrgLogoUploading] = useState(false);

  // One canonical query string for every range-aware usage request. The stat
  // cards, the trend chart and the drill-down lists all send this exact window,
  // so they can't disagree about what period is on screen. The server resolves
  // it in one place too (resolveRange in modules/assessment/profile.js).
  // Dismiss the range picker on an outside click or Escape.
  useEffect(() => {
    if (!rangePickerOpen) return undefined;
    const onPointerDown = (e) => {
      if (rangePickerRef.current && !rangePickerRef.current.contains(e.target)) {
        setRangePickerOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setRangePickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [rangePickerOpen]);

  const rangeQuery = useMemo(() => {
    if (globalRange === 'custom' && customRange.from) {
      const params = new URLSearchParams({ from: customRange.from });
      if (customRange.to) params.set('to', customRange.to);
      return params.toString();
    }
    return `days=${globalRange}`;
  }, [globalRange, customRange.from, customRange.to]);

  const [workbookDownloading, setWorkbookDownloading] = useState(false);

  /* Download the Excel report for the window currently on screen.
     `rangeQuery` is the same string the stat cards and the trend chart send, and the server
     resolves it through the same resolveRange(), so the file cannot cover a different period
     from the numbers the user was looking at when they clicked. */
  const downloadUsageWorkbook = async () => {
    if (workbookDownloading) return;
    setWorkbookDownloading(true);
    try {
      const res = await axios.get(`/api/profile/usage-workbook?${rangeQuery}`, {
        responseType: 'blob',
      });

      /* Prefer the filename the server chose — it carries the org name and the resolved
         window, which is not always the range that was asked for (All Time resolves to a
         bounded lookback). */
      const disposition = res.headers?.['content-disposition'] || '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1]) : 'TalentCrux_Report.xlsx';

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded');
    } catch (err) {
      /* The error body is a blob on this request, because responseType applies to failures
         too — read it back as text before trying to find a message in it. */
      let message = 'Could not build the report. Please try again.';
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // Not JSON — keep the generic message.
        }
      } else if (body?.message) {
        message = body.message;
      }
      toast.error(message);
    } finally {
      setWorkbookDownloading(false);
    }
  };
  // Usage & Billing is only for the org-owner admin (accountType 'org' + role
  // 'admin'), not team members / department admins.
  const tabsList = [
    { id: 'account', label: 'Account', count: 1 },
    ...(isOrgAdmin
      ? [{ id: 'usage', label: 'Usage and Billing', count: usage ? Object.values(usage).reduce((a, b) => a + b, 0) : 0 }]
      : []),
  ];
  useLayoutEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const tabEl = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (!tabEl) return;
    const measure = () => {
      const left = tabEl.offsetLeft;
      const width = tabEl.offsetWidth;
      setIndicator((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
    };
    measure();
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
    };
  }, [activeTab]);

  // Non-owner users can never sit on the Usage & Billing tab (e.g. stale state
  // or a shared link) — bounce them back to Account.
  useEffect(() => {
    if (!isOrgAdmin && activeTab === 'usage') {
      setActiveTab('account');
    }
  }, [isOrgAdmin, activeTab]);

  useEffect(() => {
    if (user) {
      setAccountForm({
        name: user.name || '',
        company: user.company || '',
        position: user.position || '',
      });
    }
  }, [user]);

  const location = useLocation();

  // Parse URL query parameters for redirection status from Google OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success === 'true') {
      toast.success('Google account integrated successfully!');
      navigate('/dashboard/profile', { replace: true });
    } else if (error) {
      if (error === 'no_refresh_token') {
        toast.error('Connection failed: Did not receive offline access token. Please disconnect and reconnect.');
      } else if (error === 'auth_failed') {
        toast.error('Authentication with Google failed.');
      } else {
        toast.error('An error occurred during Google integration.');
      }
      navigate('/dashboard/profile', { replace: true });
    }
  }, [location, navigate]);

  // Fetch email status if activeTab is 'account' or at mount
  const fetchEmailStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/integrations/email/status');
      if (data && data.connected) {
        setEmailStatus({
          loading: false,
          connected: true,
          email: data.email,
          displayName: data.displayName,
          status: data.status,
          lastError: data.lastError,
          lastUsedAt: data.lastUsedAt,
        });
        setDisplayNameInput(data.displayName || '');
      } else {
        setEmailStatus({
          loading: false,
          connected: false,
          email: '',
          displayName: '',
          status: '',
          lastError: '',
          lastUsedAt: '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch email status:', err);
      setEmailStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'account' && user?.features?.email_integration === true) {
      fetchEmailStatus();
    }
  }, [activeTab, fetchEmailStatus, user?.features?.email_integration]);

  const handleConnectGmail = () => {
    const apiBase = API_BASE_URL || 'http://localhost:5001';
    const token = localStorage.getItem('token');
    window.location.href = `${apiBase}/api/integrations/google/connect?token=${encodeURIComponent(token)}`;
  };

  const handleUpdateDisplayName = async (e) => {
    e.preventDefault();
    setIsSavingDisplayName(true);
    try {
      const { data } = await axios.post('/api/integrations/email/update-display-name', {
        displayName: displayNameInput,
      });
      toast.success(data.message || 'Display name updated!');
      setEmailStatus((prev) => ({ ...prev, displayName: data.displayName }));
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update display name';
      toast.error(errMsg);
    } finally {
      setIsSavingDisplayName(false);
    }
  };

  const handleSendTestEmail = async () => {
    setIsSendingTest(true);
    try {
      const { data } = await axios.post('/api/integrations/email/test');
      toast.success(data.message || 'Test email sent successfully! Please check your inbox.');
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to send test email';
      toast.error(errMsg);
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google Workspace/Gmail account? Candidate emails will fall back to using default TalentCrux mail system.')) {
      return;
    }
    setIsDisconnecting(true);
    try {
      await axios.post('/api/integrations/email/disconnect');
      toast.success('Gmail integration disconnected.');
      setEmailStatus({
        loading: false,
        connected: false,
        email: '',
        displayName: '',
        status: '',
        lastError: '',
        lastUsedAt: '',
      });
      setDisplayNameInput('');
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to disconnect';
      toast.error(errMsg);
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setUsageLoading(true);
        const res = await axios.get(`/api/profile/usage?${rangeQuery}`);
        setUsage(res.data);
        /* Land on the billing cycle, not All Time, the first time usage arrives.
           The credit cards are cycle-scoped by definition — the quota resets each cycle — so
           All Time divides the account's whole history by one cycle's allowance and reports
           things like "330.5 / 100 used, 0% remaining". The cycle is the only window on which
           that card is arithmetically meaningful, so it is the window the page opens on.

           THE TEST HAS TO MATCH THE ONE THAT SHOWS THE CHIP (hasBillingCycle, below). It used
           to check `billingCycleAnchor` alone, while the chip checks the anchor OR periodStart.
           getOrgPlanUsage sets the anchor from `subscription.billing_cycle_anchor` — null
           unless an admin set one — but always sets periodStart, so any org whose anchor had
           never been clicked showed a Billing Cycle chip and still opened on All Time. */
        if (!hasSetDefaultRange) {
          const q = res.data?.quotaUsage;
          if (q?.billingCycleAnchor || q?.periodStart) {
            setGlobalRange('billing');
          }
          /* Settled either way. An org with no cycle at all keeps All Time, and marking the
             default resolved stops this branch re-evaluating on every later range change. */
          setHasSetDefaultRange(true);
        }
      } catch (err) {
        console.error('Failed to fetch usage:', err);
        setUsage({
          jobPosts: 0,
          screenings: 0,
          aiInterviews: 0,
          assessments: 0,
          candidates: 0,
          shortlisted: 0,
          offered: 0,
        });
      } finally {
        setUsageLoading(false);
      }
    };
    fetchUsage();
  }, [rangeQuery, hasSetDefaultRange]);

  const fetchActivity = useCallback(async (filter, page, rangeParams = '') => {
    try {
      setActivityLoading(true);
      const rangeSuffix = rangeParams ? `&${rangeParams}` : '';
      const res = await axios.get(`/api/profile/recent-activity?filter=${filter}&page=${page}&limit=20${rangeSuffix}`);
      const { events = [], total = 0, totalPages = 1 } = res.data;
      setRecentActivity(events);
      setActivityTotal(total);
      setActivityTotalPages(totalPages);
      setActivityPage(page);
    } catch (err) {
      console.error('Failed to fetch recent activity:', err);
      setRecentActivity([]);
      setActivityTotal(0);
      setActivityTotalPages(1);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity(activityFilter, 1, rangeQuery);
  }, [activityFilter, rangeQuery, fetchActivity]);

  // Fetch yearly heatmap data on mount and react to global range changes for stats
  useEffect(() => {
    const fetchHeatmap = async () => {
      try {
        setHeatmapLoading(true);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await axios.get(`/api/profile/activity-heatmap?tz=${encodeURIComponent(tz)}&${rangeQuery}`);
        setHeatmapData(res.data.days || []);
        setHeatmapTotalActivity(res.data.totalActivity || 0);
        setPeakInterviewHour(res.data.peakInterviewHour || '—');
        setAfterOfficeHoursPercent(res.data.afterOfficeHoursPercent ?? 0);
      } catch (err) {
        console.error('Failed to fetch heatmap:', err);
        setHeatmapData([]);
        setHeatmapTotalActivity(0);
        setPeakInterviewHour('—');
        setAfterOfficeHoursPercent(0);
      } finally {
        setHeatmapLoading(false);
      }
    };
    fetchHeatmap();
  }, [rangeQuery]);

  const fetchCreditTransactions = useCallback(async (page = 1, days = 'all') => {
    try {
      setTransactionsLoading(true);
      const daysParam = days ? `&days=${days}` : '';
      const res = await axios.get(`/api/profile/credit-transactions?page=${page}&limit=10${daysParam}`);
      const { transactions = [], total = 0, totalPages = 1 } = res.data;
      setCreditTransactions(transactions);
      setTransactionsTotal(total);
      setTransactionsTotalPages(totalPages);
      setTransactionsPage(page);
    } catch (err) {
      console.error('Failed to fetch credit transactions:', err);
      setCreditTransactions([]);
      setTransactionsTotal(0);
      setTransactionsTotalPages(1);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'usage' && selectedCard === 'credits' && user?.accountType === 'org') {
      fetchCreditTransactions(transactionsPage, transactionDays);
    }
  }, [activeTab, selectedCard, transactionsPage, transactionDays, fetchCreditTransactions, user?.accountType]);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        setChartLoading(true);
        // Send the range verbatim — Number(globalRange) used to turn the
        // 'billing' preset into `days=NaN`, which the server bucketed into an
        // empty series ("No chart data available").
        const res = await axios.get(`/api/profile/activity-chart?${rangeQuery}`);
        setChartData(res.data.data || []);
        setChartGranularity(res.data.granularity || 'day');
      } catch (err) {
        console.error('Failed to fetch chart data:', err);
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchChart();
  }, [rangeQuery]);

  useEffect(() => {
    if (activeTab === 'usage') {
      const rangeParams = `?${rangeQuery}`;
      if (selectedCard === 'evaluations') {
        const fetchEvaluationsList = async () => {
          try {
            setEvaluationsListLoading(true);
            const res = await axios.get(`/api/profile/evaluations-list${rangeParams}`);
            setEvaluationsList(res.data?.items || []);
          } catch (err) {
            console.error('Failed to fetch evaluations list:', err);
            setEvaluationsList([]);
          } finally {
            setEvaluationsListLoading(false);
          }
        };
        fetchEvaluationsList();
      } else if (selectedCard === 'sourcing') {
        const fetchSourcingList = async () => {
          try {
            setSourcingListLoading(true);
            const res = await axios.get(`/api/profile/sourcing-list${rangeParams}`);
            setSourcingList(res.data?.items || []);
            setSourcingTotal(res.data?.total ?? (res.data?.items || []).length);
            setSourcingBreakdown(res.data?.sourceBreakdown || []);
          } catch (err) {
            console.error('Failed to fetch sourcing list:', err);
            setSourcingList([]);
            setSourcingTotal(0);
            setSourcingBreakdown([]);
          } finally {
            setSourcingListLoading(false);
          }
        };
        fetchSourcingList();
      } else if (selectedCard === 'funnel') {
        const fetchPipelineCandidates = async () => {
          try {
            setPipelineLoading(true);
            const res = await axios.get(`/api/profile/pipeline-candidates${rangeParams}`);
            setPipelineLists(res.data || {
              all: [],
              shortlisted: [],
              rejected: [],
            });
          } catch (err) {
            console.error('Failed to fetch pipeline candidates:', err);
            setPipelineLists({
              all: [],
              shortlisted: [],
              rejected: [],
            });
          } finally {
            setPipelineLoading(false);
          }
        };
        fetchPipelineCandidates();
      }
    }
  }, [activeTab, selectedCard, rangeQuery]);

  /* ONE request decides three visible things: the organisation's name, the Joined chip, and
     whether the Excel download button exists (`reportWorkbookAvailable`). That made a single
     failed fetch indistinguishable from a product decision — the button silently vanished, the
     name fell back to "Your organisation", the Joined chip disappeared, and nothing on the page
     said anything had gone wrong.

     It also never tried again. Every other fetch on this page keys on `rangeQuery`, so they heal
     the moment the user touches the date picker; this one is `[]`, so the degraded state stuck
     for the life of the mount and only a manual reload brought the button back.

     Easy to hit in development — saving any file under `server/` restarts nodemon, and a request
     in flight during the restart just fails — and equally easy on a phone changing networks.

     Hence: retry with backoff, and never overwrite a good response with null. A stale name is
     always better than a wrong one, and a button that exists is better than one that quietly
     does not. */
  useEffect(() => {
    let cancelled = false;

    const fetchAccountOverview = async (attempt = 0) => {
      try {
        setAccountOverviewLoading(true);
        const res = await axios.get('/api/auth/account-overview');
        if (cancelled) return;
        setAccountOverview(res.data);
        setNotifyOnApply(res.data?.notifyOnCandidateApply ?? false);
        setAutoRetryFailedInterviews(res.data?.organization?.autoRetryFailedInterviews ?? false);
        setArmNotificationsEnabled(res.data?.organization?.armNotificationsEnabled ?? true);
        setAgeGatingEnabled(res.data?.organization?.ageGatingEnabled ?? false);
        setAgeGatingRules(res.data?.organization?.ageGatingRules ?? []);
        setAccountOverviewLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (attempt < ACCOUNT_OVERVIEW_RETRIES) {
          /* Deliberately stays in the loading state across retries, so the skeletons hold
             instead of flicking to a wrong-looking header and back again. */
          setTimeout(
            () => {
              if (!cancelled) fetchAccountOverview(attempt + 1);
            },
            400 * 2 ** attempt
          );
          return;
        }
        // Out of attempts. Leave whatever we last had rather than blanking it.
        console.error('Failed to fetch account overview:', err);
        setAccountOverviewLoading(false);
      }
    };

    fetchAccountOverview();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSessions = async () => {
    if (activeTab !== 'account') return;
    setSessionsLoading(true);
    try {
      const res = await axios.get('/api/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'account') {
      fetchSessions();
    }
  }, [activeTab]);

  const handleRevokeSession = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      const res = await axios.delete(`/api/auth/sessions/${sessionId}`);
      if (res.data.revokedCurrent) {
        logout(() => navigate('/login', { replace: true }));
        toast.success('You have been signed out.');
        return;
      }
      toast.success('Session revoked.');
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke session');
    } finally {
      setRevokingId(null);
    }
  };

  const formatSessionDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString();
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountSave = async (e) => {
    e.preventDefault();
    if (accountForm.name.trim().length < 2 || accountForm.name.trim().length > 50) {
      toast.error('Name must be between 2 and 50 characters');
      return;
    }
    setAccountSaving(true);
    try {
      const success = await updateProfile({
        name: accountForm.name.trim(),
        company: accountForm.company.trim() || undefined,
        position: accountForm.position.trim() || undefined,
      });
      if (success) {
        setAccountEditing(false);
      }
    } finally {
      setAccountSaving(false);
    }
  };

  const handleNotifyToggle = async () => {
    const newValue = !notifyOnApply;
    setNotifyOnApply(newValue);
    setNotifySaving(true);
    try {
      await axios.put('/api/auth/profile', { notifyOnCandidateApply: newValue });
      toast.success(newValue ? 'Notifications enabled' : 'Notifications disabled');
    } catch (err) {
      setNotifyOnApply(!newValue);
      toast.error('Failed to update notification preference');
    } finally {
      setNotifySaving(false);
    }
  };

  const handleAutoRetryToggle = async () => {
    const newValue = !autoRetryFailedInterviews;
    setAutoRetryFailedInterviews(newValue);
    setAutoRetrySaving(true);
    try {
      await axios.put('/api/auth/org-settings', { autoRetryFailedInterviews: newValue });
      toast.success(newValue ? 'Auto-retry enabled for organization' : 'Auto-retry disabled for organization');
    } catch (err) {
      setAutoRetryFailedInterviews(!newValue);
      toast.error('Failed to update auto-retry preference');
    } finally {
      setAutoRetrySaving(false);
    }
  };

  const handleArmNotificationsToggle = async () => {
    const newValue = !armNotificationsEnabled;
    setArmNotificationsEnabled(newValue);
    setArmNotificationsSaving(true);
    try {
      await axios.put('/api/auth/org-settings', { armNotificationsEnabled: newValue });
      toast.success(newValue ? 'ARM notifications enabled' : 'ARM notifications disabled');
    } catch (err) {
      setArmNotificationsEnabled(!newValue);
      toast.error('Failed to update ARM notifications preference');
    } finally {
      setArmNotificationsSaving(false);
    }
  };

  const handleAgeGatingToggle = async () => {
    const newValue = !ageGatingEnabled;
    setAgeGatingEnabled(newValue);
    
    let updatedRules = ageGatingRules;
    if (newValue && ageGatingRules.length === 0) {
      updatedRules = [
        { keyword: 'Restaurant General Manager', maxAge: '' },
        { keyword: 'Assistant Restaurant Manager', maxAge: '' },
        { keyword: 'Shift Manager', maxAge: '' }
      ];
      setAgeGatingRules(updatedRules);
    }
    
    setAgeGatingSaving(true);
    try {
      await axios.put('/api/auth/org-settings', { ageGatingEnabled: newValue, ageGatingRules: updatedRules });
      toast.success(newValue ? 'Age Gating enabled' : 'Age Gating disabled');
    } catch (err) {
      setAgeGatingEnabled(!newValue);
      setAgeGatingRules(ageGatingRules); // revert to original rules on fail
      toast.error('Failed to update Age Gating preference');
    } finally {
      setAgeGatingSaving(false);
    }
  };

  const saveAgeGatingRules = async (rulesToSave) => {
    setAgeGatingSaving(true);
    try {
      await axios.put('/api/auth/org-settings', { ageGatingRules: rulesToSave });
      toast.success('Age Gating rules saved');
    } catch (err) {
      toast.error('Failed to save Age Gating rules');
    } finally {
      setAgeGatingSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(passwordForm.newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/\d/.test(passwordForm.newPassword)) {
      setPasswordError('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword)) {
      setPasswordError('Password must contain at least one special character');
      return;
    }
    setPasswordSaving(true);
    try {
      const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (success) {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const usageRows = usage
    ? [
      { label: 'Job posts', value: usage.jobPosts },
      { label: 'Screenings', value: usage.screenings },
      { label: 'AI interviews', value: usage.aiInterviews },
      { label: 'Assessments', value: usage.assessments },
      { label: 'Candidates', value: usage.candidates },
    ]
    : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const roleLabel =
    user?.role === 'admin'
      ? 'Organization Admin'
      : user?.role === 'department_admin' || user?.role === 'dept_admin'
        ? 'Team Admin'
        : 'Member';

  const orgDisplayName =
    (accountOverview?.organization && accountOverview.organization.name) ||
    user?.company ||
    'Your organisation';

  const initials = (orgDisplayName || 'TG')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  const orgLogoUrl = accountOverview?.organization?.logoUrl || null;
  const orgPlan = accountOverview?.organization || null;
  const planTotalCredits = orgPlan?.totalAiCredits ?? null;
  const planLabel = (raw) => {
    if (!raw) return 'Starter';
    const v = String(raw).toLowerCase();
    if (v === 'core') return 'Core';
    if (v === 'custom' || v === 'enterprise') return 'Enterprise';
    return v.charAt(0).toUpperCase() + v.slice(1);
  };
  const billingLabel = (raw) => {
    if (!raw) return 'Monthly';
    const v = String(raw).toLowerCase();
    if (v === 'yearly') return 'Yearly';
    return 'Monthly';
  };

  // V1 quota data from usage API
  const quotaUsage = usage?.quotaUsage || null;
  const hasV1Quotas = !!(quotaUsage?.video_interviews?.quota != null || quotaUsage?.audio_interviews?.quota != null);

  // Legacy credit tracking (fallback for old orgs)
  const aiUsedForPlan = usage?.aiInterviews ?? 0;
  const aiPercentForPlan = planTotalCredits
    ? Math.min(100, Math.round((aiUsedForPlan / planTotalCredits) * 100))
    : null;

  const PLAN_PRICES = { starter: 24999, core: 44999 };
  const planKey = (orgPlan?.plan || '').toLowerCase();
  const planMonthlyPrice = orgPlan?.planMonthlyPrice ?? null;
  // For starter/core, always use canonical price from plans.js (source of truth); enterprise uses DB value
  const effectiveMonthlyPrice =
    PLAN_PRICES[planKey] ?? planMonthlyPrice ?? null;

  // V2 credit-based usage
  const isV2Credits = quotaUsage?.version === 'v2';

  /* ── Billing cycle ────────────────────────────────────────────────────────
     The billing cycle is the org's CURRENT recurring period, not everything
     since billing began: getBillingPeriodBounds() on the server rolls the
     admin-set anchor forward by billing_cycle_duration_months until the window
     contains today. The credit quota is consumed and reset against exactly
     this window, which is why it's the default range when an anchor exists. */
  const billingAnchor = quotaUsage?.billingCycleAnchor || null;
  const billingPeriodStart = quotaUsage?.periodStart || null;
  const billingPeriodEnd = quotaUsage?.periodEnd || null;
  const hasBillingCycle = !!(billingAnchor || billingPeriodStart);

  // periodEnd is the EXCLUSIVE end of the cycle: anchor + duration, i.e. the
  // instant the quota resets, which is also the NEXT cycle's periodStart.
  // Rendering it raw reads as though the cycle spans a year *and a day*
  // (Aug 1 2026 - Aug 1 2027) and as though consecutive cycles overlap on that
  // date, so labels show the last day the cycle actually covers instead.
  const billingPeriodLastDay = (() => {
    if (!billingPeriodEnd) return null;
    const end = new Date(billingPeriodEnd);
    if (Number.isNaN(end.getTime())) return null;
    return new Date(end.getTime() - 1);
  })();

  const shortDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Named ranges the calendar can't express, shown as chips inside the picker.
  const rangeModePresets = [
    { label: '24H', value: '1' },
    { label: '7D', value: '7' },
    { label: '30D', value: '30' },
    { label: '90D', value: '90' },
    ...(hasBillingCycle
      ? [
        {
          label: 'Billing Cycle',
          value: 'billing',
          hint: billingPeriodStart
            ? `Current billing period: ${shortDate(billingPeriodStart)} – ${shortDate(
              billingPeriodLastDay
            )}. Credits reset on ${shortDate(billingPeriodEnd)}.`
            : 'Your current billing period',
        },
      ]
      : []),
    { label: 'All Time', value: 'all' },
  ];

  // What the picker's trigger button reads. Always resolves to a concrete
  // window so it's never ambiguous which period the numbers cover.
  const rangeTriggerLabel = (() => {
    if (globalRange === 'custom' && customRange.from) {
      const to = customRange.to || customRange.from;
      return `${shortDate(customRange.from)} – ${shortDate(to)}`;
    }
    if (globalRange === 'billing') {
      return billingPeriodStart
        ? `Billing cycle · ${shortDate(billingPeriodStart)} – ${shortDate(billingPeriodLastDay)}`
        : 'Billing cycle';
    }
    if (globalRange === 'all') return 'All time';
    const preset = rangeModePresets.find((p) => p.value === globalRange);
    if (globalRange === '1') return 'Last 24 hours';
    return preset ? `Last ${preset.label.replace('D', ' days')}` : 'Select range';
  })();

  // Compute a simple "next plan" suggestion for upgrade card
  let nextPlanLabel = null;
  let nextPlanCredits = null;
  let nextPlanPrice = null;
  if (orgPlan?.plan === 'starter') {
    nextPlanLabel = 'Core';
    nextPlanCredits = 250;
    nextPlanPrice = 44999;
  }

  const handleOrgLogoClick = () => {
    if (!canEditProfile) return;
    if (!accountOverview?.organization?.id) {
      toast.error('No organisation attached to this account yet.');
      return;
    }
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const handleOrgLogoChange = async (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setOrgLogoUploading(true);
      const formData = new FormData();
      formData.append('logo', file);
      const res = await axios.post('/api/auth/org-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.logoUrl;
      if (newUrl) {
        setAccountOverview((prev) =>
          prev && prev.organization
            ? {
              ...prev,
              organization: {
                ...prev.organization,
                logoUrl: newUrl,
              },
            }
            : prev
        );
      }
      toast.success('Organisation logo updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setOrgLogoUploading(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC] text-gray-900">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Top section: profile hero */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="relative group" onClick={handleOrgLogoClick}>
              <div
                className={`h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg cursor-pointer overflow-hidden relative ${orgLogoUrl ? 'bg-transparent border border-gray-200' : 'bg-[#007BFF]'
                  }`}
              >
                {accountOverviewLoading ? (
                  <SkeletonBlock className="h-full w-full rounded-none" />
                ) : orgLogoUrl ? (
                  <img
                    src={orgLogoUrl}
                    alt="Organisation logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-semibold text-white">{initials}</span>
                )}
                {canEditProfile && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] font-medium text-white transition-opacity">
                    {orgLogoUploading ? 'Uploading…' : 'Change logo'}
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-1">
                Account center
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#242124] tracking-tight">
                {accountOverviewLoading ? <SkeletonBlock className="h-8 w-64 max-w-full" /> : orgDisplayName}
              </h1>
              <p className="mt-1 text-sm text-gray-600">{user?.email}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {roleLabel && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3EEFF] border border-[#E0D5FF] px-3 py-1 text-xs font-medium text-[#4B2DBA] shadow-sm">
                    {roleLabel}
                  </span>
                )}
                {accountOverviewLoading ? (
                  <SkeletonBlock className="h-6 w-56 rounded-full" />
                ) : accountOverview?.teams && accountOverview.teams.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
                    Teams: {accountOverview.teams.map((t) => t.name).join(', ')}
                  </span>
                )}
                {accountOverviewLoading ? (
                  <SkeletonBlock className="h-6 w-36 rounded-full" />
                ) : accountOverview?.joinedAt && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                    Joined {formatDate(accountOverview.joinedAt)}
                  </span>
                )}
                {/* Two separate facts: when billing began (fixed) and the
                    current cycle the credit quota resets against (rolling). */}
                {billingAnchor && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF4FF] border border-[#CCE4FF] px-3 py-1 text-xs font-medium text-[#0B5CAD]">
                    <CreditCardIcon className="h-3.5 w-3.5" />
                    Billing since {formatDate(billingAnchor)}
                  </span>
                )}

              </div>
            </div>
          </div>

          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleOrgLogoChange}
          />
          <div className="flex flex-col items-start lg:items-end gap-2">
            <button
              type="button"
              onClick={() => setShowCreditsModal(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#007BFF] bg-transparent px-3 py-2 text-xs sm:text-sm font-medium text-[#007BFF] hover:bg-blue-50/50 hover:border-[#0066cc] hover:text-[#0066cc] transition-all duration-200"
            >
              <CreditCardIcon className="h-4 w-4" />
              Buy More Credits
            </button>
            <button
              type="button"
              onClick={() => logout(() => navigate('/login'))}
              className="inline-flex items-center gap-1.5 rounded-xl border border-red-500 bg-transparent px-3 py-2 text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 hover:border-red-600 transition"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 pb-0">
          <div
            ref={tabsRef}
            className="relative flex items-center gap-4 sm:gap-6 min-w-0 bg-transparent"
          >
            {tabsList.map((tab) => (
              <button
                key={tab.id}
                type="button"
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative z-10 px-1.5 pb-1.5 pt-1 text-xs sm:text-sm font-medium transition-colors duration-200 whitespace-nowrap ${activeTab === tab.id
                  ? 'text-[#242124]'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {tab.label}
              </button>
            ))}
            <motion.div
              className="absolute bottom-0 left-0 h-0.5 rounded-full bg-[#8472B3]"
              initial={false}
              animate={{
                left: indicator.left,
                width: indicator.width > 0 ? indicator.width : 80,
              }}
              transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30,
              }}
              style={{ willChange: 'left, width' }}
            />
          </div>
        </div>

        {/* Optional search for admins - hidden on plan tab now */}
        {canEditProfile && false && (
          <div className="w-full max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search within your account…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
              />
            </div>
          </div>
        )}

        {/* Content - full width */}
        <div className="w-full space-y-6">
          {/* Account tab */}
          {activeTab === 'account' && (
            <div className="space-y-5">
              {/* Profile card */}
              <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-blue-50 p-1.5">
                      <UserCircleIcon className="h-4 w-4 text-[#007BFF]" />
                    </div>
                    <h2 className="text-sm font-semibold text-[#242124]">Profile</h2>
                  </div>
                  {canEditProfile && (
                    <button
                      type="button"
                      onClick={() => setAccountEditing((v) => !v)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-[#007BFF] hover:text-[#007BFF] hover:bg-blue-50/50 transition"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                      {accountEditing ? 'Done' : 'Edit'}
                    </button>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    {
                      label: 'Full name',
                      editable: true,
                      name: 'name',
                      value: accountForm.name,
                      display: accountForm.name || '—',
                    },
                    { label: 'Email', display: user?.email || '—' },
                    {
                      label: 'Organization',
                      display: accountOverviewLoading ? '—' : accountOverview?.organization?.name || '—',
                    },
                    { label: 'Role', display: roleLabel },
                    {
                      label: 'Joined',
                      display: formatDate(accountOverview?.joinedAt || user?.createdAt),
                    },
                    {
                      label: 'Last active',
                      display: accountOverviewLoading ? '—' : formatSessionDate(accountOverview?.lastActiveAt),
                    },
                  ].map((field) => (
                    <div key={field.label}>
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                        {field.label}
                      </p>
                      {field.editable && canEditProfile && accountEditing ? (
                        <input
                          type="text"
                          name={field.name}
                          value={field.value}
                          onChange={handleAccountChange}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
                          minLength={2}
                          maxLength={50}
                        />
                      ) : (
                        <p className="text-sm font-medium text-[#242124]">
                          {accountOverviewLoading &&
                            (field.label === 'Organization' ||
                              field.label === 'Joined' ||
                              field.label === 'Last active') ? (
                            <SkeletonBlock className="h-4 w-36 max-w-full" />
                          ) : (
                            field.display
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {canEditProfile && accountEditing && (
                  <div className="mt-5 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setAccountForm({
                          name: user?.name || '',
                          company: user?.company || '',
                          position: user?.position || '',
                        });
                        setAccountEditing(false);
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAccountSave}
                      disabled={accountSaving}
                      className="rounded-lg bg-[#007BFF] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#0066cc] disabled:opacity-70 transition"
                    >
                      {accountSaving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Org contacts */}
              <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="rounded-lg bg-violet-50 p-1.5">
                    <UsersIcon className="h-4 w-4 text-violet-600" />
                  </div>
                  <h2 className="text-sm font-semibold text-[#242124]">Org contacts</h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-lg border border-gray-50 bg-gray-50/50 px-4 py-3">
                    <div className="h-8 w-8 rounded-full bg-[#007BFF]/10 flex items-center justify-center shrink-0">
                      <UserCircleIcon className="h-4 w-4 text-[#007BFF]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Org admin</p>
                      <p className="text-sm font-medium text-[#242124] truncate">
                        {accountOverviewLoading ? (
                          <SkeletonBlock className="h-4 w-28" />
                        ) : (
                          accountOverview?.orgAdmin?.name || '—'
                        )}
                      </p>
                      {accountOverviewLoading ? (
                        <SkeletonBlock className="h-3 w-44 mt-1" />
                      ) : accountOverview?.orgAdmin?.email ? (
                        <p className="text-[11px] text-gray-500 truncate">{accountOverview.orgAdmin.email}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-gray-50 bg-gray-50/50 px-4 py-3">
                    <div className="h-8 w-8 rounded-full bg-violet-100/60 flex items-center justify-center shrink-0">
                      <UsersIcon className="h-4 w-4 text-violet-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Team admin</p>
                      <p className="text-sm font-medium text-[#242124] truncate">
                        {accountOverviewLoading ? (
                          <SkeletonBlock className="h-4 w-28" />
                        ) : (
                          accountOverview?.teamAdmin?.name || '—'
                        )}
                      </p>
                      {accountOverviewLoading ? (
                        <SkeletonBlock className="h-3 w-44 mt-1" />
                      ) : accountOverview?.teamAdmin?.email ? (
                        <p className="text-[11px] text-gray-500 truncate">{accountOverview.teamAdmin.email}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Integration Card */}
              <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="rounded-lg bg-blue-50 p-1.5">
                    <svg className="h-4 w-4 text-[#007BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-[#242124]">Email Integration</h2>
                </div>
                {!user?.features?.email_integration ? (
                  <div className="relative overflow-hidden rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-gray-50/50 p-5 sm:p-6 shadow-inner">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-blue-50/30 rounded-full blur-2xl pointer-events-none" />
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 relative z-10">
                      <div className="flex items-start gap-3.5 max-w-xl">
                        <div className="rounded-xl bg-blue-50 border border-blue-100 p-2.5 text-blue-600 shrink-0 shadow-sm">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-sm tracking-tight">Custom Domain Sender</h3>
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-0.5 uppercase tracking-wider">
                              Premium Add-on
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            Send candidate invitations, assessment links, and notifications directly from your corporate GSuite / Google Workspace domain instead of TalentCrux.
                          </p>
                        </div>
                      </div>

                      <div className="shrink-0">
                        <a
                          href="mailto:sales@talentcrux.ai?subject=Enable Custom Sender Email Integration"
                          className="inline-flex items-center justify-center bg-[#007BFF] hover:bg-[#0066cc] text-white font-bold py-2 px-4 rounded-xl text-xs shadow-sm hover:shadow transition-all gap-1.5 whitespace-nowrap"
                        >
                          <span>Request Access</span>
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                ) : emailStatus.loading ? (
                  <div className="flex flex-col items-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-[#007BFF]" />
                    <p className="text-xs text-gray-500 mt-2">Checking integration status...</p>
                  </div>
                ) : !emailStatus.connected ? (
                  <div className="space-y-4">
                    <div className="bg-[#F7F9FC] border border-gray-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="max-w-xl">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1">Connect Google Workspace / Gmail</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Send candidate invitations, assessments, and notifications directly from your corporate email address instead of <span className="font-mono bg-gray-100 px-1 py-0.5 rounded text-[11px]">no-reply@talentcrux.ai</span>.
                        </p>
                      </div>
                      <div className="shrink-0">
                        <button
                          type="button"
                          onClick={handleConnectGmail}
                          className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-xl border border-gray-200 text-xs shadow-sm hover:shadow transition-all gap-2"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                            />
                          </svg>
                          <span>Connect Google Account</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="border border-gray-100 rounded-xl p-4 bg-[#F7F9FC]">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600 shrink-0 border border-green-100">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900 text-sm">{emailStatus.email}</h3>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${emailStatus.status === 'active'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                : 'bg-red-50 text-red-700 border border-red-100'
                                }`}>
                                {emailStatus.status === 'active' ? 'Active' : 'Connection Error'}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Connected to Google Workspace.
                              {emailStatus.lastUsedAt && ` Last used: ${new Date(emailStatus.lastUsedAt).toLocaleDateString()}`}
                            </p>
                          </div>
                        </div>

                        {isOrgAdmin && (
                          <button
                            type="button"
                            onClick={handleDisconnect}
                            disabled={isDisconnecting}
                            className="inline-flex items-center justify-center border border-red-200 hover:bg-red-50 text-red-600 font-medium py-1.5 px-3 rounded-lg text-xs transition-colors gap-1.5 shrink-0 disabled:opacity-50"
                          >
                            {isDisconnecting ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-600 border-t-transparent" />
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                            )}
                            Disconnect
                          </button>
                        )}
                      </div>

                      {emailStatus.status !== 'active' && emailStatus.lastError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700 flex items-start gap-2 animate-fadeIn">
                          <svg className="h-4 w-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <p className="font-semibold">Recent Connection Error:</p>
                            <p className="mt-0.5">{emailStatus.lastError}</p>
                            {isOrgAdmin && (
                              <button
                                type="button"
                                onClick={handleConnectGmail}
                                className="mt-1.5 text-red-800 font-bold hover:underline"
                              >
                                Reconnect Google Account
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Settings Options */}
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Display Name settings */}
                      <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-xs mb-1">Sender Display Name</h3>
                          <p className="text-[11px] text-gray-500 mb-4">
                            Customize the sender name candidates will see in their inboxes (e.g. "Acme Corp Hiring").
                          </p>
                        </div>

                        <form onSubmit={handleUpdateDisplayName} className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={displayNameInput}
                            onChange={(e) => setDisplayNameInput(e.target.value)}
                            placeholder="e.g. Acme Hiring Team"
                            disabled={!isOrgAdmin}
                            className="flex-1 border border-gray-200 rounded-lg py-1.5 px-3 text-xs focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] hover:border-gray-300 transition-colors disabled:bg-gray-50"
                          />
                          {isOrgAdmin && (
                            <button
                              type="submit"
                              disabled={isSavingDisplayName}
                              className="inline-flex items-center justify-center bg-[#007BFF] hover:bg-[#0066cc] text-white font-semibold py-1.5 px-3 rounded-lg text-xs transition-colors gap-1 disabled:opacity-50 shrink-0"
                            >
                              {isSavingDisplayName ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                              ) : (
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                </svg>
                              )}
                              Save
                            </button>
                          )}
                        </form>
                      </div>

                      {/* Connection Testing */}
                      <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-xs mb-1">Verify Delivery</h3>
                          <p className="text-[11px] text-gray-500 mb-4">
                            Send a test email using your integrated Google connection to verify delivery to your inbox.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={handleSendTestEmail}
                          disabled={isSendingTest || emailStatus.status !== 'active'}
                          className="inline-flex items-center justify-center border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold py-1.5 px-3 rounded-lg text-xs transition-colors gap-1.5 disabled:opacity-50 self-start"
                        >
                          {isSendingTest ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-500 border-t-transparent" />
                          ) : (
                            <svg className="h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          )}
                          Send Test Email to {user?.email}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Password & Security */}
              <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-amber-50 p-1.5">
                      <ShieldCheckIcon className="h-4 w-4 text-amber-600" />
                    </div>
                    <h2 className="text-sm font-semibold text-[#242124]">Password & security</h2>
                  </div>
                  {!showPasswordForm && (
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-[#007BFF] hover:text-[#007BFF] hover:bg-blue-50/50 transition"
                    >
                      Change password
                    </button>
                  )}
                </div>
                {showPasswordForm && (
                  <form onSubmit={handlePasswordSubmit} className="space-y-3 max-w-md">
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Current password"
                      className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] focus:bg-white transition"
                      required
                    />
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="New password (min 8 characters)"
                      className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] focus:bg-white transition"
                      minLength={6}
                    />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm new password"
                      className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] focus:bg-white transition"
                    />
                    {passwordError && (
                      <p className="text-xs text-red-600">{passwordError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={passwordSaving}
                        className="rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0066cc] disabled:opacity-70 transition"
                      >
                        {passwordSaving ? 'Updating…' : 'Update password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                          setPasswordError('');
                        }}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                {!showPasswordForm && (
                  <p className="text-xs text-gray-500">Your password was last changed a while ago. We recommend updating it regularly.</p>
                )}
              </div>


              {/* Notification preferences */}
              <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="rounded-lg bg-blue-50 p-1.5">
                    <BriefcaseIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <h2 className="text-sm font-semibold text-[#242124]">Notifications</h2>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">New candidate application</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Get an email when a candidate completes a screening
                      {accountOverview?.role === 'admin'
                        ? ' across your entire organisation.'
                        : ' in your team.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={notifyOnApply}
                    disabled={notifySaving}
                    onClick={handleNotifyToggle}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2 disabled:opacity-60 ${notifyOnApply ? 'bg-[#007BFF]' : 'bg-gray-200'
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${notifyOnApply ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              </div>

              {/* Organisation Settings */}
              {isOrgAdmin && (
                <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm mt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="rounded-lg bg-blue-50 p-1.5">
                      <BriefcaseIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <h2 className="text-sm font-semibold text-[#242124]">Organisation Settings</h2>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">Auto-Retry Failed Interviews</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Automatically resend the screening link (via email & WhatsApp) if a candidate faces technical issues and drops off (scores 0). Limited to 1 auto-retry per candidate.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={autoRetryFailedInterviews}
                      disabled={autoRetrySaving}
                      onClick={handleAutoRetryToggle}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2 disabled:opacity-60 ${autoRetryFailedInterviews ? 'bg-[#007BFF]' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${autoRetryFailedInterviews ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>
                  {user?.features?.store_decision_emails && (
                    <>
                      <hr className="my-4 border-gray-100" />
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800">Area Manager Notifications</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Automatically send emails to Area Managers when candidates complete (or timeout) their screening links.
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={armNotificationsEnabled}
                          disabled={armNotificationsSaving}
                          onClick={handleArmNotificationsToggle}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2 disabled:opacity-60 ${armNotificationsEnabled ? 'bg-[#007BFF]' : 'bg-gray-200'
                            }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${armNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                          />
                        </button>
                      </div>
                    </>
                  )}

                  {/* Age Gating Configuration */}
                  <hr className="my-4 border-gray-100" />
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">Candidate Age Gating</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Automatically reject candidates if their age exceeds the limit for specific roles.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={ageGatingEnabled}
                      disabled={ageGatingSaving}
                      onClick={handleAgeGatingToggle}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2 disabled:opacity-60 ${ageGatingEnabled ? 'bg-[#007BFF]' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${ageGatingEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>

                  {ageGatingEnabled && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-800 mb-3">Age Gating Rules</h4>
                      
                      {ageGatingRules.length === 0 && (
                        <p className="text-xs text-gray-500 mb-3">No rules configured. Candidates will not be rejected.</p>
                      )}

                      <div className="space-y-3 mb-4">
                        {ageGatingRules.map((rule, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="flex-1">
                              <input
                                type="text"
                                placeholder="Job Title Keyword (e.g. Shift Manager)"
                                value={rule.keyword}
                                onChange={(e) => {
                                  const newRules = [...ageGatingRules];
                                  newRules[idx].keyword = e.target.value;
                                  setAgeGatingRules(newRules);
                                }}
                                disabled={['Restaurant General Manager', 'Assistant Restaurant Manager', 'Shift Manager'].includes(rule.keyword)}
                                className={`w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-[#007BFF] focus:ring-[#007BFF] ${['Restaurant General Manager', 'Assistant Restaurant Manager', 'Shift Manager'].includes(rule.keyword) ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''}`}
                              />
                            </div>
                            <div className="w-24">
                              <input
                                type="number"
                                placeholder="Max Age"
                                value={rule.maxAge === null ? '' : rule.maxAge}
                                onChange={(e) => {
                                  const newRules = [...ageGatingRules];
                                  newRules[idx].maxAge = e.target.value ? parseInt(e.target.value, 10) : '';
                                  setAgeGatingRules(newRules);
                                }}
                                className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-[#007BFF] focus:ring-[#007BFF]"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newRules = ageGatingRules.filter((_, i) => i !== idx);
                                setAgeGatingRules(newRules);
                              }}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setAgeGatingRules([...ageGatingRules, { keyword: '', maxAge: '' }])}
                          className="text-sm text-[#007BFF] font-medium hover:text-blue-700"
                        >
                          + Add Rule
                        </button>

                        <button
                          type="button"
                          disabled={ageGatingSaving}
                          onClick={() => saveAgeGatingRules(ageGatingRules)}
                          className="px-4 py-1.5 text-sm font-medium rounded-md shadow-sm text-white bg-[#007BFF] hover:bg-blue-600 disabled:opacity-50"
                        >
                          {ageGatingSaving ? 'Saving...' : 'Save Rules'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Session history */}
              {(() => {
                const browserLogos = {
                  chrome: '/images/chrome-icon.svg',
                  firefox: '/images/firefox-icon.svg',
                  safari: '/images/safari-icon.svg',
                  // Devicon gh path for Edge fails on jsDelivr; file is simple-icons Edge from jsDelivr npm.
                  edge: '/images/edge-icon.svg',
                  opera: '/images/opera-icon.svg',
                  brave: '/images/brave-icon.svg',
                };
                const getBrowserLogo = (s) => {
                  const b = (s.browser || s.device || '').toLowerCase();
                  if (b.includes('brave')) return browserLogos.brave;
                  if (b.includes('edge')) return browserLogos.edge;
                  if (b.includes('opera')) return browserLogos.opera;
                  if (b.includes('firefox')) return browserLogos.firefox;
                  if (b.includes('safari') && !b.includes('chrome')) return browserLogos.safari;
                  if (b.includes('chrome')) return browserLogos.chrome;
                  return null;
                };
                const getBrowserName = (s) => s.browser || s.device || 'Unknown browser';
                const getOsLabel = (s) => s.os || '';
                const getSessionAge = (dateStr) => {
                  if (!dateStr) return '';
                  const diff = Date.now() - new Date(dateStr).getTime();
                  const mins = Math.floor(diff / 60000);
                  if (mins < 1) return 'Just now';
                  if (mins < 60) return `${mins} min ago`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs} hr ago`;
                  const days = Math.floor(hrs / 24);
                  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
                  const months = Math.floor(days / 30);
                  return `${months} month${months > 1 ? 's' : ''} ago`;
                };

                return (
                  <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="rounded-lg bg-emerald-50 p-1.5">
                          <ClockIcon className="h-4 w-4 text-emerald-600" />
                        </div>
                        <h2 className="text-sm font-semibold text-[#242124]">Session history</h2>
                      </div>
                      <span className="text-[11px] text-gray-400 font-medium">
                        Max 6 sessions
                      </span>
                    </div>
                    {sessionsLoading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, idx) => (
                          <div
                            key={`session-skeleton-${idx}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-gray-50 bg-gray-50/50 px-4 py-3.5"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <SkeletonBlock className="h-10 w-10 rounded-full" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <SkeletonBlock className="h-4 w-48 max-w-full" />
                                  <SkeletonBlock className="h-5 w-12 rounded-full" />
                                </div>
                                <SkeletonBlock className="h-3 w-32" />
                              </div>
                            </div>
                            <SkeletonBlock className="h-7 w-16 rounded-lg" />
                          </div>
                        ))}
                      </div>
                    ) : sessions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                        <ClockIcon className="h-8 w-8 text-gray-300 mb-2" />
                        <p className="text-sm font-medium text-gray-500">Only this device</p>
                        <p className="text-xs text-gray-400 mt-0.5">You are currently signed in on this device only.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {sessions.map((session) => {
                          const logo = getBrowserLogo(session);
                          const browserName = getBrowserName(session);
                          const osLabel = getOsLabel(session);
                          const deviceDesc = osLabel ? `${browserName} on ${osLabel}` : browserName;
                          return (
                            <div
                              key={session.id}
                              className="flex items-center justify-between gap-3 rounded-lg border border-gray-50 bg-gray-50/50 px-4 py-3.5 hover:bg-gray-100/50 transition-colors"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
                                  {logo ? (
                                    <img src={logo} alt={browserName} className="h-6 w-6 object-contain" />
                                  ) : (
                                    <span className="text-lg">💻</span>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-[#242124] truncate">
                                      {deviceDesc}
                                    </p>
                                    {session.current && (
                                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-100">
                                        Active
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {session.location && (
                                      <>
                                        <span className="text-[11px] text-gray-500">{session.location}</span>
                                        <span className="text-gray-300">·</span>
                                      </>
                                    )}
                                    <span className="text-[11px] text-gray-500">
                                      {session.current ? 'Currently active' : getSessionAge(session.lastActiveAt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {!session.current && (
                                <button
                                  type="button"
                                  onClick={() => handleRevokeSession(session.id)}
                                  disabled={revokingId === session.id}
                                  className="rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-100 transition disabled:opacity-50 shrink-0"
                                >
                                  {revokingId === session.id ? 'Revoking…' : 'Revoke'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Usage tab */}
          {activeTab === 'usage' && (() => {
            // Top cards: use real operational counts from the /usage API endpoint.
            // The Plan Usage card below still reads from the billing-period ledger
            // (quotaUsage) for accurate credit/quota tracking.
            const aiInterviewTotal = usage?.aiInterviews ?? 0;
            // Interviews actually charged a credit, and the ones that were NOT
            // (candidate dropped off before the credit threshold). Both come from
            // the server computed over the SAME session set + date window as
            // aiInterviewTotal, so billed + free === conducted, always.
            const billedInterviewTotal = usage?.billedInterviews ?? aiInterviewTotal;
            const freeInterviewTotal = usage?.freeInterviews ?? 0;
            const assessmentsTotal = usage?.assessments ?? 0;

            // Compute AI credits variables for Card 1
            let creditsRemaining = 0;
            let creditsTotal = 0;
            let creditsUsed = 0;
            let creditPct = 0;

            /* THIS CARD IS ALWAYS CYCLE-SCOPED, AND DELIBERATELY IGNORES THE RANGE PICKER.
               A balance is a fact about the account right now, not about a chosen window. Every
               branch below used to derive `creditsUsed` from `usage`, which IS windowed, so the
               card computed "plan minus what was spent in the selected range" and called the
               result the remaining balance. On Last 7 days that reported 99.5 of 100 credits
               left on an account with 62; on All Time it reported "330.5 / 100 used, 0%
               remaining", because a whole account history does not fit in one cycle's quota.

               It is the same arithmetic the weekly client report already refuses to print —
               see core/sheets/creditRegister.js: "plan minus this-week does not equal the
               balance (1000 - 49 != 887)". The balance needs cycle-to-date, which only the
               billing engine has, so the figures are read from it rather than recomputed.
               `getOrgPlanUsage` is called with no window by /profile/usage, so `quotaUsage`
               is the current billing cycle regardless of what the picker says. */
            if (isV2Credits) {
              creditsTotal = quotaUsage?.credits || 0;
              creditsUsed = quotaUsage?.creditsUsed ?? 0;
              creditsRemaining = quotaUsage?.creditsRemaining ?? Math.max(0, creditsTotal - creditsUsed);
              creditPct = creditsTotal > 0 ? Math.min(100, Math.round((creditsUsed / creditsTotal) * 100)) : 0;
            } else if (planTotalCredits) {
              /* Legacy v1 quotas carry their own cycle-scoped `used` per type. Prefer those;
                 `aiUsedForPlan` is the windowed count and is only a last resort. */
              const v1Used =
                (quotaUsage?.video_interviews?.used ?? null) !== null ||
                (quotaUsage?.audio_interviews?.used ?? null) !== null
                  ? (quotaUsage?.video_interviews?.used ?? 0) + (quotaUsage?.audio_interviews?.used ?? 0)
                  : null;
              creditsTotal = planTotalCredits;
              creditsUsed = v1Used ?? aiUsedForPlan;
              creditsRemaining = Math.max(0, creditsTotal - creditsUsed);
              creditPct = creditsTotal > 0 ? Math.min(100, Math.round((creditsUsed / creditsTotal) * 100)) : 0;
            } else {
              /* No plan and no quota data. There is no balance to state, so the bar is the
                 windowed interview count against a nominal 100 and nothing here is billing. */
              creditsUsed = usage?.aiInterviews ?? 0;
              creditsTotal = 100;
              creditsRemaining = Math.max(0, creditsTotal - creditsUsed);
              creditPct = Math.min(100, Math.round((creditsUsed / creditsTotal) * 100));
            }

            // Evaluations (AI Interviews + Assessments) for Card 2
            const evaluationsTotal = aiInterviewTotal + assessmentsTotal;

            // Candidates vs Shortlisted for Card 4
            const candidatesTotal = usage?.candidates ?? 0;
            const shortlistedTotal = usage?.shortlisted ?? 0;
            const offeredTotal = usage?.offered ?? 0;
            const shortlistRate = candidatesTotal > 0 ? ((shortlistedTotal / candidatesTotal) * 100).toFixed(1) : "0.0";

            const activityTypeConfig = {
              interview: { icon: MicrophoneIcon, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'AI Interview' },
              job_post: { icon: BriefcaseIcon, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Job Post' },
              screening: { icon: FunnelIcon, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Screening' },
              assessment: { icon: AcademicCapIcon, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Assessment' },
            };

            const filterOptions = [
              { key: 'all', label: 'All' },
              { key: 'interview', label: 'Interviews' },
              { key: 'assessment', label: 'Assessments' },
              { key: 'job_post', label: 'Job Posts' },
              { key: 'screening', label: 'Screenings' },
            ];

            const formatRelTime = (dateStr) => {
              if (!dateStr) return '';
              const diff = Date.now() - new Date(dateStr).getTime();
              const mins = Math.floor(diff / 60000);
              if (mins < 1) return 'Just now';
              if (mins < 60) return `${mins}m ago`;
              const hrs = Math.floor(mins / 60);
              if (hrs < 24) return `${hrs}h ago`;
              const days = Math.floor(hrs / 24);
              if (days < 7) return `${days}d ago`;
              return formatDate(dateStr);
            };

            const chartDateFormatter = (tick) => {
              if (typeof tick === 'string' && /^\d{1,2}:\d{2}$/.test(tick)) return tick;
              const d = new Date(tick);
              return `${d.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]}`;
            };

            return (
              <div className="space-y-5">
                {/* Global Time Range Selector — one trigger, presets and the
                    custom calendar together inside the popover. */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Usage Overview</h3>
                    <p className="text-xs text-gray-500">Key metrics and activities for your organization</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                  {/* Sits beside the picker on purpose: the button downloads the range that
                      is already selected here, so there is no second date control that can
                      disagree with the first. */}
                  {accountOverview?.reportWorkbookAvailable && (
                    <button
                      type="button"
                      onClick={downloadUsageWorkbook}
                      disabled={workbookDownloading}
                      title={`Download the Excel report for ${rangeTriggerLabel}`}
                      className="flex h-10 shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 text-xs font-semibold text-gray-700 shadow-sm hover:border-[#007BFF]/50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    >
                      {workbookDownloading ? (
                        <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-200 border-t-[#007BFF]" />
                      ) : (
                        <ArrowDownTrayIcon className="h-4 w-4 text-[#007BFF] shrink-0" />
                      )}
                      <span className="whitespace-nowrap">
                        {workbookDownloading ? 'Preparing…' : 'Excel'}
                      </span>
                    </button>
                  )}
                  <div className="relative shrink-0" ref={rangePickerRef}>
                    <button
                      type="button"
                      onClick={() => setRangePickerOpen((prev) => !prev)}
                      className="flex h-10 w-full sm:w-auto sm:min-w-[260px] items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3.5 text-xs font-semibold text-gray-700 shadow-sm hover:border-[#007BFF]/50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 transition-all"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <CalendarDaysIcon className="h-4 w-4 text-[#007BFF] shrink-0" />
                        <span className="truncate font-bold text-gray-900">{rangeTriggerLabel}</span>
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200 ${rangePickerOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {rangePickerOpen && (
                      <DateRangePopover
                        className="left-auto right-0"
                        initialRange={globalRange === 'custom' ? customRange : undefined}
                        modePresets={rangeModePresets}
                        activeMode={globalRange === 'custom' ? null : globalRange}
                        // No day-window presets here: the modePresets above
                        // cover the relative ranges, and the calendar covers
                        // anything custom.
                        presets={[]}
                        onClose={() => setRangePickerOpen(false)}
                        onApply={(next) => {
                          setHasSetDefaultRange(true);
                          if (next.mode) {
                            setGlobalRange(next.mode);
                          } else {
                            setCustomRange({ from: next.from, to: next.to });
                            setGlobalRange('custom');
                          }
                          setRangePickerOpen(false);
                        }}
                      />
                    )}
                  </div>
                  </div>
                </div>

                {usageLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-500 text-sm">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-[#007BFF]" />
                    <p className="mt-3">Loading dashboard metrics…</p>
                  </div>
                ) : (
                  <>
                    {/* Redesigned Stat cards row: 5 high-fidelity premium cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                      {/* Card 1: AI Credits Quota (Purple Theme) */}
                      <div
                        onClick={() => setSelectedCard('credits')}
                        className={`group relative rounded-[28px] border p-6 cursor-pointer select-none transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[160px] ${selectedCard === 'credits'
                          ? 'border-transparent shadow-lg shadow-indigo-100/40 ring-2 ring-[#5C53A3] ring-offset-1 bg-gradient-to-br from-[#F5F2FF] to-[#ECE7FF] -translate-y-1 scale-[1.01]'
                          : 'border-[#DDD5FF] bg-gradient-to-br from-[#ECE7FF]/20 to-[#F3F0FF]/20 hover:border-[#5C53A3]/60 hover:shadow-md hover:shadow-indigo-50/30 hover:-translate-y-1'
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs font-semibold text-[#5C53A3] tracking-wide uppercase">AI Credits Quota</p>
                            <h4 className="text-3xl font-extrabold text-[#2F2A5C] tracking-tight mt-1">
                              {creditsRemaining.toLocaleString()}
                            </h4>
                            <p className="text-[11px] font-medium text-[#7D73C0] mt-0.5">Credits Remaining</p>
                            {/* Says so out loud, because this is the one card on the row that
                                does not move when the range changes. Without the line, a
                                reader who switches range and sees four cards change and this
                                one hold still reads it as stale rather than as cycle-scoped. */}
                            <p className="text-[10px] font-medium text-[#9A92CE] mt-0.5">
                              {hasBillingCycle && billingPeriodStart
                                ? `This billing cycle · ${shortDate(billingPeriodStart)} – ${shortDate(billingPeriodLastDay)}`
                                : 'This billing cycle'}
                            </p>
                          </div>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#5C53A3] text-white shadow-sm shrink-0">
                            <CreditCardIcon className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="flex justify-between items-center text-[10px] text-[#5C53A3] font-semibold mb-1">
                            <span>{creditsUsed.toLocaleString()} / {creditsTotal.toLocaleString()} used</span>
                            <span>{100 - creditPct}% remaining</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-[#E1DBFF] overflow-hidden">
                            <motion.div
                              className="h-full bg-[#5C53A3] rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${creditPct}%` }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                          </div>
                        </div>

                        {/* Elegant bottom-right bar illustration */}
                        <svg className="absolute bottom-0 right-0 w-24 h-16 pointer-events-none transition-transform duration-500 group-hover:translate-x-1" viewBox="0 0 100 60" fill="currentColor">
                          <defs>
                            <linearGradient id="barGradPurple" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#5C53A3" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#5C53A3" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <rect x="20" y="30" width="8" height="20" rx="3" fill="url(#barGradPurple)" />
                          <rect x="35" y="15" width="8" height="35" rx="3" fill="url(#barGradPurple)" />
                          <rect x="50" y="25" width="8" height="25" rx="3" fill="url(#barGradPurple)" />
                          <rect x="65" y="8" width="8" height="42" rx="3" fill="url(#barGradPurple)" />
                          <rect x="80" y="20" width="8" height="30" rx="3" fill="url(#barGradPurple)" />
                        </svg>
                      </div>

                      {/* Card 2a: AI Interviews (Teal Theme) */}
                      <div
                        onClick={() => {
                          setSelectedCard('evaluations');
                          setEvaluationsFilter('interview');
                        }}
                        className={`group relative rounded-[28px] border p-6 cursor-pointer select-none transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[160px] ${selectedCard === 'evaluations' && evaluationsFilter === 'interview'
                          ? 'border-transparent shadow-lg shadow-emerald-100/40 ring-2 ring-[#10B981] ring-offset-1 bg-gradient-to-br from-[#EEFAF2] to-[#E6F7ED] -translate-y-1 scale-[1.01]'
                          : 'border-[#C3ECD0] bg-gradient-to-br from-[#E6F7ED]/20 to-[#EEFAF2]/20 hover:border-[#10B981]/60 hover:shadow-md hover:shadow-emerald-50/30 hover:-translate-y-1'
                          }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-[#1B6F3E] tracking-wide uppercase">AI Interviews</p>
                            <h4 className="text-3xl font-extrabold text-[#0E4724] tracking-tight mt-1">
                              {aiInterviewTotal.toLocaleString()}
                            </h4>
                            <p className="text-[11px] font-medium text-[#2E8B57] mt-0.5">Interviews Conducted</p>
                            {freeInterviewTotal > 0 && (
                              <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-2xl bg-white/70 border border-gray-200 px-2 py-0.5 max-w-full">
                                <span className="text-[10px] font-semibold text-emerald-700 whitespace-nowrap">
                                  {billedInterviewTotal.toLocaleString()} charged
                                </span>
                                <span className="text-gray-300">·</span>
                                <span className="text-[10px] font-semibold text-red-600 whitespace-nowrap">
                                  {freeInterviewTotal.toLocaleString()} not charged
                                </span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => e.stopPropagation()}
                                  data-tooltip-id="free-interviews-tooltip"
                                  data-tooltip-content={`${freeInterviewTotal} interview${freeInterviewTotal === 1 ? '' : 's'} ran but ${freeInterviewTotal === 1 ? 'was' : 'were'} not charged — ${freeInterviewTotal === 1 ? 'it' : 'they'} failed on our side or ended too early to score.`}
                                  className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 cursor-help"
                                >
                                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#10b981] text-white shadow-sm shrink-0">
                            <VideoCameraIcon className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="flex justify-between items-center text-[10px] text-[#1B6F3E] font-semibold mb-1">
                            <span>{aiInterviewTotal.toLocaleString()} conducted</span>
                            <span>{evaluationsTotal > 0 ? Math.round((aiInterviewTotal / evaluationsTotal) * 100) : 0}% of total</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-[#D2F3DD] overflow-hidden">
                            <motion.div
                              className="h-full bg-[#10B981] rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${evaluationsTotal > 0 ? (aiInterviewTotal / evaluationsTotal) * 100 : 0}%` }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                          </div>
                        </div>

                        {/* Elegant bottom-right bar illustration */}
                        <svg className="absolute bottom-0 right-0 w-24 h-16 pointer-events-none transition-transform duration-500 group-hover:translate-x-1" viewBox="0 0 100 60" fill="currentColor">
                          <defs>
                            <linearGradient id="barGradGreen" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#10b981" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <rect x="20" y="20" width="8" height="30" rx="3" fill="url(#barGradGreen)" />
                          <rect x="35" y="32" width="8" height="18" rx="3" fill="url(#barGradGreen)" />
                          <rect x="50" y="12" width="8" height="38" rx="3" fill="url(#barGradGreen)" />
                          <rect x="65" y="24" width="8" height="26" rx="3" fill="url(#barGradGreen)" />
                          <rect x="80" y="16" width="8" height="34" rx="3" fill="url(#barGradGreen)" />
                        </svg>
                      </div>

                      {/* Card 2b: Assessments (Amber Theme) */}
                      <div
                        onClick={() => {
                          setSelectedCard('evaluations');
                          setEvaluationsFilter('assessment');
                        }}
                        className={`group relative rounded-[28px] border p-6 cursor-pointer select-none transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[160px] ${selectedCard === 'evaluations' && evaluationsFilter === 'assessment'
                          ? 'border-transparent shadow-lg shadow-amber-100/40 ring-2 ring-[#D97706] ring-offset-1 bg-gradient-to-br from-[#FEFCE8] to-[#FEF3C7] -translate-y-1 scale-[1.01]'
                          : 'border-[#FDE68A] bg-gradient-to-br from-[#FEF3C7]/20 to-[#FEFCE8]/20 hover:border-[#D97706]/60 hover:shadow-md hover:shadow-amber-50/30 hover:-translate-y-1'
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs font-semibold text-[#92400E] tracking-wide uppercase">Assessments</p>
                            <h4 className="text-3xl font-extrabold text-[#78350F] tracking-tight mt-1">
                              {assessmentsTotal.toLocaleString()}
                            </h4>
                            <p className="text-[11px] font-medium text-[#B45309] mt-0.5">Assessments Conducted</p>
                          </div>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D97706] text-white shadow-sm shrink-0">
                            <AcademicCapIcon className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="flex justify-between items-center text-[10px] text-[#B45309] font-semibold mb-1">
                            <span>{assessmentsTotal.toLocaleString()} conducted</span>
                            <span>{evaluationsTotal > 0 ? Math.round((assessmentsTotal / evaluationsTotal) * 100) : 0}% of total</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-[#FEF3C7] overflow-hidden">
                            <motion.div
                              className="h-full bg-[#D97706] rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${evaluationsTotal > 0 ? (assessmentsTotal / evaluationsTotal) * 100 : 0}%` }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                          </div>
                        </div>

                        {/* Elegant bottom-right bar illustration */}
                        <svg className="absolute bottom-0 right-0 w-24 h-16 pointer-events-none transition-transform duration-500 group-hover:translate-x-1" viewBox="0 0 100 60" fill="currentColor">
                          <defs>
                            <linearGradient id="barGradAmber" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#D97706" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#D97706" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <rect x="20" y="25" width="8" height="25" rx="3" fill="url(#barGradAmber)" />
                          <rect x="35" y="10" width="8" height="40" rx="3" fill="url(#barGradAmber)" />
                          <rect x="50" y="20" width="8" height="30" rx="3" fill="url(#barGradAmber)" />
                          <rect x="65" y="30" width="8" height="20" rx="3" fill="url(#barGradAmber)" />
                          <rect x="80" y="15" width="8" height="35" rx="3" fill="url(#barGradAmber)" />
                        </svg>
                      </div>

                      {/* Card 3: Resumes & Sourcing (Orange Theme) */}
                      <div
                        onClick={() => setSelectedCard('sourcing')}
                        className={`group relative rounded-[28px] border p-6 cursor-pointer select-none transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[160px] ${selectedCard === 'sourcing'
                          ? 'border-transparent shadow-lg shadow-orange-100/40 ring-2 ring-[#f97316] ring-offset-1 bg-gradient-to-br from-[#FFF8F3] to-[#FFEFE6] -translate-y-1 scale-[1.01]'
                          : 'border-[#FFD9C4] bg-gradient-to-br from-[#FFEFE6]/20 to-[#FFF8F3]/20 hover:border-[#f97316]/60 hover:shadow-md hover:shadow-orange-50/30 hover:-translate-y-1'
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs font-semibold text-[#9A4C1B] tracking-wide uppercase">Resumes</p>
                            <h4 className="text-3xl font-extrabold text-[#5C2707] tracking-tight mt-1">
                              {(usage?.screenedResumes ?? 0).toLocaleString()}
                            </h4>
                            <p className="text-[11px] font-medium text-[#B26435] mt-0.5">Resumes Screened</p>
                          </div>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f97316] text-white shadow-sm shrink-0">
                            <DocumentTextIcon className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="flex justify-between items-center text-[10px] text-[#9A4C1B] font-semibold mb-1">
                            <span>{(usage?.resumeUploadsTotal ?? 0).toLocaleString()} uploads</span>
                            <span>parsed &amp; ranked</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-[#FFDFCE] overflow-hidden">
                            <motion.div
                              className="h-full bg-[#f97316] rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: "100%" }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                          </div>
                        </div>

                        {/* Elegant bottom-right bar illustration */}
                        <svg className="absolute bottom-0 right-0 w-24 h-16 pointer-events-none transition-transform duration-500 group-hover:translate-x-1" viewBox="0 0 100 60" fill="currentColor">
                          <defs>
                            <linearGradient id="barGradOrange" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f97316" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <rect x="20" y="15" width="8" height="35" rx="3" fill="url(#barGradOrange)" />
                          <rect x="35" y="28" width="8" height="22" rx="3" fill="url(#barGradOrange)" />
                          <rect x="50" y="8" width="8" height="42" rx="3" fill="url(#barGradOrange)" />
                          <rect x="65" y="32" width="8" height="18" rx="3" fill="url(#barGradOrange)" />
                          <rect x="80" y="20" width="8" height="30" rx="3" fill="url(#barGradOrange)" />
                        </svg>
                      </div>

                      {/* Card 4: Candidates & Shortlist (Blue Theme) */}
                      <div
                        onClick={() => setSelectedCard('funnel')}
                        className={`group relative rounded-[28px] border p-6 cursor-pointer select-none transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[160px] ${selectedCard === 'funnel'
                          ? 'border-transparent shadow-lg shadow-blue-100/40 ring-2 ring-[#3b82f6] ring-offset-1 bg-gradient-to-br from-[#F2F8FF] to-[#E8F4FF] -translate-y-1 scale-[1.01]'
                          : 'border-[#C8E2FF] bg-gradient-to-br from-[#E8F4FF]/20 to-[#F2F8FF]/20 hover:border-[#3b82f6]/60 hover:shadow-md hover:shadow-blue-50/30 hover:-translate-y-1'
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs font-semibold text-[#1D5E9A] tracking-wide uppercase">Pipeline Funnel</p>
                            <h4 className="text-3xl font-extrabold text-[#0D385F] tracking-tight mt-1">
                              {candidatesTotal.toLocaleString()}
                            </h4>
                            <p className="text-[11px] font-medium text-[#2C74B3] mt-0.5">Total Candidates</p>
                          </div>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#3b82f6] text-white shadow-sm shrink-0">
                            <UsersIcon className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-4 flex items-center">
                          <div className="text-[10px] text-[#1D5E9A] font-semibold">
                            <span>{shortlistedTotal.toLocaleString()} Selected · {offeredTotal.toLocaleString()} Offered</span>
                          </div>
                        </div>

                        {/* Elegant bottom-right bar illustration */}
                        <svg className="absolute bottom-0 right-0 w-24 h-16 pointer-events-none transition-transform duration-500 group-hover:translate-x-1" viewBox="0 0 100 60" fill="currentColor">
                          <defs>
                            <linearGradient id="barGradBlue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <rect x="20" y="8" width="8" height="42" rx="3" fill="url(#barGradBlue)" />
                          <rect x="35" y="20" width="8" height="30" rx="3" fill="url(#barGradBlue)" />
                          <rect x="50" y="32" width="8" height="18" rx="3" fill="url(#barGradBlue)" />
                          <rect x="65" y="15" width="8" height="35" rx="3" fill="url(#barGradBlue)" />
                          <rect x="80" y="25" width="8" height="25" rx="3" fill="url(#barGradBlue)" />
                        </svg>
                      </div>
                    </div>

                    {/* Activity Trend Graph card */}
                    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-sm font-semibold text-[#242124]">Activity trend</h3>
                          <p className="text-xs text-gray-500">
                            Visual activity volume of candidate screenings and postings
                            {chartData.length > 0 && (
                              <span className="text-gray-400">
                                {' · '}
                                {chartGranularity === 'hour'
                                  ? 'hourly'
                                  : chartGranularity === 'week'
                                    ? 'weekly totals'
                                    : chartGranularity === 'month'
                                      ? 'monthly totals'
                                      : 'daily'}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="h-64">
                        {chartLoading ? (
                          <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-[#007BFF]" />
                          </div>
                        ) : chartData.length === 0 ? (
                          <div className="flex items-center justify-center h-52 text-gray-400 text-xs">
                            No chart data available.
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis
                                dataKey="date"
                                tickFormatter={chartDateFormatter}
                                tick={{ fontSize: 10, fill: '#9ca3af' }}
                                axisLine={false}
                                tickLine={false}
                                interval="preserveStartEnd"
                              />
                              <YAxis
                                tick={{ fontSize: 10, fill: '#9ca3af' }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                              />
                              <Tooltip
                                contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}
                                labelFormatter={(l) => chartDateFormatter(l)}
                              />
                              <Legend
                                content={({ payload }) => (
                                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2" style={{ fontSize: 11 }}>
                                    {payload?.map((entry) => {
                                      const key = entry.dataKey;
                                      const visible = chartSeriesVisible[key];
                                      return (
                                        <button
                                          key={key}
                                          type="button"
                                          onClick={() => setChartSeriesVisible((prev) => ({ ...prev, [key]: !prev[key] }))}
                                          className={`inline-flex items-center gap-1.5 cursor-pointer select-none rounded px-1.5 py-0.5 transition-opacity hover:opacity-100 ${visible ? 'opacity-100' : 'opacity-40'}`}
                                        >
                                          <span className="rounded-full w-2 h-2 shrink-0" style={{ backgroundColor: entry.color }} />
                                          <span>{entry.value}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              />
                              <Line type="monotone" dataKey="interviews" name="Interviews" stroke="#10b981" strokeWidth={2} dot={false} hide={!chartSeriesVisible.interviews} />
                              <Line type="monotone" dataKey="jobPosts" name="Job Posts" stroke="#3b82f6" strokeWidth={2} dot={false} hide={!chartSeriesVisible.jobPosts} />
                              <Line type="monotone" dataKey="screenings" name="Screenings" stroke="#8b5cf6" strokeWidth={2} dot={false} hide={!chartSeriesVisible.screenings} />
                              <Line type="monotone" dataKey="assessments" name="Assessments" stroke="#f59e0b" strokeWidth={2} dot={false} hide={!chartSeriesVisible.assessments} />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* Details Overlay Modal */}
                    {selectedCard && createPortal(
                      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setSelectedCard(null)}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 20 }}
                          transition={{ duration: 0.2 }}
                          className="bg-white rounded-[32px] border border-gray-100 shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden relative flex flex-col"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Header & Close Button */}
                          <div className="flex items-center justify-between border-b border-gray-100 p-6 sm:p-8 pb-4 shrink-0">
                            <div>
                              <h2 className="text-lg font-bold text-gray-900 font-sans">
                                {selectedCard === 'credits' && 'AI Credits & Ledger Details'}
                                {selectedCard === 'evaluations' && (
                                  evaluationsFilter === 'interview'
                                    ? 'AI Interviews Details'
                                    : evaluationsFilter === 'assessment'
                                      ? 'Assessments Details'
                                      : 'Evaluations Conducted Details'
                                )}
                                {selectedCard === 'sourcing' && 'Resumes Details'}
                                {selectedCard === 'funnel' && 'Recruitment Funnel & Pipeline Details'}
                              </h2>
                              <p className="text-xs text-gray-500">Detailed overview of usage logs and metrics</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedCard(null)}
                              className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>

                          {/* Modal Body */}
                          <div className="overflow-y-auto scrollbar-thin-light p-6 sm:p-8 pt-4 pb-8 space-y-6 flex-1 pr-4 sm:pr-6 mr-1">
                            {selectedCard === 'credits' && (
                              <div className="grid gap-8 lg:grid-cols-[1.1fr_1.3fr]">
                                {/* Left: Plan Credits Ledger & Guide */}
                                <div className="space-y-6">
                                  <div className="space-y-4">
                                    <div className="flex items-center gap-2.5">
                                      <div className="rounded-xl bg-[#F0EEFF] p-2 text-[#5C53A3]">
                                        <CreditCardIcon className="h-5 w-5" />
                                      </div>
                                      <div>
                                        <h3 className="text-base font-bold text-gray-900">Plan Credits Ledger</h3>
                                        <p className="text-xs text-gray-500">Billing details and current quota allocations</p>
                                      </div>
                                    </div>

                                    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5 space-y-4 shadow-sm">
                                      <div>
                                        <div className="flex justify-between items-center text-xs font-semibold text-gray-700 mb-1.5">
                                          <span>Total Credits Used <span className="text-gray-400 font-normal">(all time)</span></span>
                                          <span className="font-bold">{creditsUsed.toLocaleString()} / {creditsTotal.toLocaleString()} ({creditPct}%)</span>
                                        </div>
                                        <div className="h-2.5 w-full rounded-full bg-gray-200/60 overflow-hidden">
                                          <motion.div
                                            className={`h-full rounded-full ${creditPct >= 90 ? 'bg-red-500' : 'bg-[#5C53A3]'}`}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${creditPct}%` }}
                                            transition={{ duration: 0.8, ease: 'easeOut' }}
                                          />
                                        </div>
                                      </div>

                                      <div className="divide-y divide-gray-100 text-xs">
                                        <div className="flex justify-between py-2.5">
                                          <span className="text-gray-500 font-medium">Plan Level</span>
                                          <span className="font-semibold text-gray-800">{planLabel(orgPlan?.plan)} ({billingLabel(orgPlan?.billingPeriod)})</span>
                                        </div>
                                        {isV2Credits ? (
                                          <>
                                            <div className="flex justify-between py-2.5">
                                              <span className="text-gray-500 font-medium">AI Interviews Charged <span className="text-gray-400 font-normal">(all time)</span></span>
                                              <span className="font-semibold text-gray-800">{(usage?.billedInterviews ?? usage?.aiInterviews ?? 0).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between py-2.5">
                                              <span className="text-gray-500 font-medium">Assessments Conducted <span className="text-gray-400 font-normal">(all time)</span></span>
                                              <span className="font-semibold text-gray-800">{(usage?.assessments ?? 0).toLocaleString()}</span>
                                            </div>
                                          </>
                                        ) : (
                                          <div className="flex justify-between py-2.5">
                                            <span className="text-gray-500 font-medium">AI Interview Sessions</span>
                                            <span className="font-semibold text-gray-800">{aiUsedForPlan.toLocaleString()}</span>
                                          </div>
                                        )}
                                        <div className="flex justify-between py-2.5">
                                          <span className="text-gray-500 font-medium">Resume Database Entries</span>
                                          <span className="font-semibold text-gray-800">{(usage?.screenedResumes ?? 0).toLocaleString()} <span className="text-gray-400 font-normal">(unlimited)</span></span>
                                        </div>
                                        {quotaUsage?.teamUsers?.quota != null && (
                                          <div className="flex justify-between py-2.5">
                                            <span className="text-gray-500 font-medium">Active Team Users</span>
                                            <span className="font-semibold text-gray-800">{quotaUsage.teamUsers.used ?? 0} / {quotaUsage.teamUsers.quota}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <h4 className="text-xs font-bold text-gray-900">Credit Consumption Guide</h4>
                                    <div className="rounded-2xl border border-[#E1DBFF] bg-[#F7F6FF]/60 p-5 space-y-4">
                                      <p className="text-xs text-[#2F2A5C] leading-relaxed">
                                        TalentCrux uses a simple credit allocation model. Actions consume credits from your active plan balance, while other core platform features are unlimited and free.
                                      </p>

                                      <div className="space-y-2.5">
                                        {(() => {
                                          const costs = quotaUsage?.creditCosts || { interview: 1.0, assessment: 0.5, resume: 0.0 };
                                          const isResumeFree = !(costs.resume > 0);
                                          return [
                                            {
                                              icon: VideoCameraIcon,
                                              title: 'Video / Audio AI Interviews',
                                              desc: 'Billed per candidate session',
                                              cost: `${(costs.interview ?? 1.0).toFixed(1)} Cr`,
                                              isFree: false
                                            },
                                            {
                                              icon: AcademicCapIcon,
                                              title: 'Custom Assessments (MCQs, Coding)',
                                              desc: 'Billed per candidate test invite',
                                              cost: `${(costs.assessment ?? 0.5).toFixed(1)} Cr`,
                                              isFree: false
                                            },
                                            {
                                              icon: DocumentTextIcon,
                                              title: 'Resume Screening & Scoring',
                                              desc: 'Unlimited resume screening',
                                              cost: isResumeFree ? 'Free' : `${costs.resume.toFixed(1)} Cr`,
                                              isFree: isResumeFree
                                            }
                                          ].map((item, idx) => {
                                            const ItemIcon = item.icon;
                                            return (
                                              <div
                                                key={idx}
                                                className="flex items-center justify-between p-3 rounded-xl border border-white bg-white/70 shadow-xs hover:shadow-sm hover:border-indigo-100/50 transition duration-200"
                                              >
                                                <div className="flex items-center gap-3">
                                                  <div className={`rounded-xl p-2 shrink-0 ${item.isFree ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-[#5C53A3]'
                                                    }`}>
                                                    <ItemIcon className="h-5 w-5" />
                                                  </div>
                                                  <div>
                                                    <p className="text-xs font-bold text-gray-900">{item.title}</p>
                                                    <p className="text-[10px] text-gray-500 mt-0.5">{item.desc}</p>
                                                  </div>
                                                </div>
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold shadow-xs ${item.isFree
                                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/30'
                                                  : 'bg-indigo-50 text-[#5C53A3] border border-indigo-100/30'
                                                  }`}>
                                                  {item.cost}
                                                </span>
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Credit Transaction History */}
                                <div className="space-y-4">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                      <h3 className="text-base font-bold text-gray-900 mb-0.5">Credit Transaction History</h3>
                                      <p className="text-xs text-gray-500">Real-time details of where your credits are spent</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={transactionDays}
                                        onChange={(e) => {
                                          setTransactionDays(e.target.value);
                                          setTransactionsPage(1);
                                        }}
                                        className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 outline-none hover:border-gray-300 hover:bg-gray-50 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition shadow-sm cursor-pointer"
                                      >
                                        <option value="billing">Billing Period</option>
                                        <option value="7">Last 7 Days</option>
                                        <option value="30">Last 30 Days</option>
                                        <option value="90">Last 90 Days</option>
                                        <option value="all">All Time</option>
                                      </select>
                                      {transactionsTotal > 0 && (
                                        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold text-[#5C53A3] whitespace-nowrap">
                                          {transactionsTotal} events
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden flex flex-col min-h-[360px] justify-between">
                                    {transactionsLoading ? (
                                      <div className="flex items-center justify-center py-24 text-gray-400 text-xs flex-grow">
                                        <div className="inline-block animate-spin rounded-full h-7 w-7 border-[3px] border-gray-100 border-t-indigo-600" />
                                      </div>
                                    ) : creditTransactions.length === 0 ? (
                                      <div className="flex flex-col items-center justify-center py-20 text-gray-400 flex-grow text-center px-4">
                                        <ClockIcon className="h-8 w-8 text-gray-300 mb-2" />
                                        <p className="text-xs font-semibold text-gray-500">No transactions yet</p>
                                        <p className="text-[10px] text-gray-400 mt-1 max-w-[220px]">
                                          Your credit transaction ledger is currently empty. Run assessments or interviews to see logs.
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="divide-y divide-gray-50 flex-grow">
                                        {creditTransactions.map((tx) => {
                                          const cfg = transactionIcons[tx.type] || {
                                            icon: CreditCardIcon,
                                            bg: 'bg-gray-100',
                                            color: 'text-gray-500',
                                          };
                                          const TxIcon = cfg.icon;
                                          return (
                                            <div key={tx._id} className="flex items-center justify-between p-4 hover:bg-gray-50/50 transition duration-150">
                                              <div className="flex items-center gap-3 min-w-0">
                                                <div className={`rounded-xl p-2 shrink-0 ${cfg.bg} ${cfg.color}`}>
                                                  <TxIcon className="h-4 w-4" />
                                                </div>
                                                <div className="min-w-0">
                                                  <p className="text-xs font-bold text-gray-900 truncate">
                                                    {tx.details?.targetLink ? (
                                                      <a
                                                        href={tx.details.targetLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-indigo-600 hover:underline transition duration-150 cursor-pointer"
                                                        title={`View details for ${tx.details.name}`}
                                                      >
                                                        {tx.details.name}
                                                      </a>
                                                    ) : (
                                                      tx.details?.name
                                                    )}
                                                  </p>
                                                  <p
                                                    className="text-[10px] text-gray-500 truncate mt-0.5 max-w-[150px] sm:max-w-[240px] md:max-w-[360px]"
                                                    title={tx.details?.subtext}
                                                  >
                                                    {tx.details?.subtext}
                                                  </p>
                                                </div>
                                              </div>

                                              <div className="flex items-center gap-3 shrink-0">
                                                <div className="text-right">
                                                   <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[9px] font-bold ${tx.charged === false
                                                     ? 'bg-red-50 text-red-600 border border-red-100/30 cursor-help'
                                                     : tx.withinQuota
                                                       ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/30'
                                                       : 'bg-amber-50 text-amber-700 border border-amber-100/30'
                                                     }`}
                                                     {...(tx.charged === false ? {
                                                       'data-tooltip-id': 'free-interviews-tooltip',
                                                       'data-tooltip-content': 'Candidate left the interview early — not charged.'
                                                     } : {})}
                                                   >
                                                     {tx.charged === false ? 'Not charged' : tx.withinQuota ? 'Quota' : 'Overage'}
                                                     {tx.charged === false && (
                                                       <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5 shrink-0 text-red-500/80">
                                                         <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                                                       </svg>
                                                     )}
                                                   </span>
                                                  <p className="text-[9px] text-gray-400 mt-0.5">
                                                    {formatSessionDate(tx.occurredAt)}
                                                  </p>
                                                </div>
                                                <span className={`text-xs font-extrabold ${tx.charged === false ? 'text-red-500' : tx.creditCost > 0 ? 'text-[#5C53A3]' : 'text-gray-400'}`}>
                                                  {tx.charged === false ? '0.0 Cr' : tx.creditCost > 0 ? `-${tx.creditCost.toFixed(1)} Cr` : 'Free'}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Pagination Footer */}
                                    {transactionsTotalPages > 1 && (
                                      <div className="bg-gray-50/60 px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                                        <button
                                          type="button"
                                          disabled={transactionsPage === 1 || transactionsLoading}
                                          onClick={() => setTransactionsPage((p) => Math.max(1, p - 1))}
                                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition shadow-sm"
                                        >
                                          Previous
                                        </button>
                                        <span className="text-[10px] font-medium text-gray-500">
                                          Page {transactionsPage} of {transactionsTotalPages}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={transactionsPage === transactionsTotalPages || transactionsLoading}
                                          onClick={() => setTransactionsPage((p) => Math.min(transactionsTotalPages, p + 1))}
                                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition shadow-sm"
                                        >
                                          Next
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedCard === 'evaluations' && (() => {
                              const filteredEvaluations = evaluationsList.filter((item) => {
                                if (evaluationsFilter === 'all') return true;
                                return item.type === evaluationsFilter;
                              });

                              const statusConfig = {
                                completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
                                in_progress: { label: 'In Progress', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
                                pending: { label: 'Pending', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
                                abandoned: { label: 'Abandoned', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100' },
                                invited: { label: 'Invited', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-100' },
                                started: { label: 'Started', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
                                submitted: { label: 'Submitted', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
                              };

                              const handleEvalItemClick = (item) => {
                                if (item.type === 'interview') {
                                  // Build a candidate-like object for the hook
                                  const candidateObj = {
                                    _id: item.candidateId,
                                    email: item.candidateEmail,
                                    screeningId: item.screeningId,
                                    interviewSessionId: item.sessionId,
                                    interviewId: item.interviewId,
                                  };
                                  openInterviewSessionForCandidate(candidateObj);
                                } else if (item.type === 'assessment') {
                                  const candidateObj = {
                                    _id: item.candidateId || item.invitedCandidateId,
                                    email: item.candidateEmail,
                                    screeningId: item.screeningId,
                                    assessmentId: item.assessmentId,
                                  };
                                  handleInlineViewAssessmentScore(candidateObj);
                                }
                              };

                              return (
                                <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
                                  {/* Left: Chart + Stats Cards */}
                                  <div className="space-y-4">
                                    {/* Stat Cards */}
                                    <div className={`grid gap-3 ${evaluationsFilter === 'all' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                      {(evaluationsFilter === 'all' || evaluationsFilter === 'interview') && (
                                        <motion.div
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.1 }}
                                          className="rounded-2xl border border-emerald-100/80 bg-gradient-to-br from-emerald-50/80 to-white p-4 text-center shadow-sm"
                                        >
                                          <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100/70">
                                            <VideoCameraIcon className="h-5 w-5 text-emerald-600" />
                                          </div>
                                          <p className="text-2xl font-extrabold text-[#0E4724]">{aiInterviewTotal.toLocaleString()}</p>
                                          <p className="text-[11px] font-semibold text-emerald-700 mt-0.5">AI Interviews</p>
                                        </motion.div>
                                      )}
                                      {(evaluationsFilter === 'all' || evaluationsFilter === 'assessment') && (
                                        <motion.div
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.15 }}
                                          className="rounded-2xl border border-amber-100/80 bg-gradient-to-br from-amber-50/80 to-white p-4 text-center shadow-sm"
                                        >
                                          <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100/70">
                                            <AcademicCapIcon className="h-5 w-5 text-amber-600" />
                                          </div>
                                          <p className="text-2xl font-extrabold text-[#5C2707]">{assessmentsTotal.toLocaleString()}</p>
                                          <p className="text-[11px] font-semibold text-amber-700 mt-0.5">Assessments</p>
                                        </motion.div>
                                      )}
                                    </div>

                                    {/* Activity Trend Chart */}
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h3 className="text-sm font-bold text-gray-900">Activity Trend</h3>
                                          <p className="text-[10px] text-gray-500">Interview & assessment volume</p>
                                        </div>
                                      </div>

                                      <div className="rounded-2xl border border-gray-100 p-3 bg-white shadow-sm">
                                        {chartLoading ? (
                                          <div className="flex items-center justify-center h-40 text-gray-400 text-xs">
                                            <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-emerald-500" />
                                          </div>
                                        ) : chartData.length === 0 ? (
                                          <div className="flex items-center justify-center h-40 text-gray-400 text-xs">No trend data</div>
                                        ) : (
                                          <ResponsiveContainer width="100%" height={160}>
                                            <LineChart data={chartData} margin={{ top: 5, right: 8, left: -25, bottom: 0 }}>
                                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                                              <XAxis dataKey="date" tickFormatter={chartDateFormatter} tick={{ fontSize: 8, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                              <YAxis tick={{ fontSize: 8, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                              <Tooltip contentStyle={{ fontSize: 10, borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,.04)' }} labelFormatter={(l) => chartDateFormatter(l)} />
                                              {(evaluationsFilter === 'all' || evaluationsFilter === 'interview') && (
                                                <Line type="monotone" dataKey="interviews" name="Interviews" stroke="#10b981" strokeWidth={2} dot={false} />
                                              )}
                                              {(evaluationsFilter === 'all' || evaluationsFilter === 'assessment') && (
                                                <Line type="monotone" dataKey="assessments" name="Assessments" stroke="#f59e0b" strokeWidth={2} dot={false} />
                                              )}
                                            </LineChart>
                                          </ResponsiveContainer>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Right: Recent Evaluations Feed */}
                                  <div className="space-y-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                      <div>
                                        <h3 className="text-sm font-bold text-gray-900">Recent Evaluations</h3>
                                        <p className="text-[10px] text-gray-500">Click to view detailed results</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {evaluationsFilter === 'all' && (
                                          <select
                                            value={evaluationsFilter}
                                            onChange={(e) => setEvaluationsFilter(e.target.value)}
                                            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 outline-none hover:border-gray-300 hover:bg-gray-50 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition shadow-sm cursor-pointer"
                                          >
                                            <option value="all">All Types</option>
                                            <option value="interview">Interviews</option>
                                            <option value="assessment">Assessments</option>
                                          </select>
                                        )}
                                        {filteredEvaluations.length > 0 && (
                                          <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 whitespace-nowrap">
                                            {filteredEvaluations.length} results
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden flex flex-col" style={{ maxHeight: 380 }}>
                                      {evaluationsListLoading ? (
                                        <div className="flex items-center justify-center py-20 text-gray-400 text-xs flex-grow">
                                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-emerald-500" />
                                        </div>
                                      ) : evaluationsList.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 flex-grow text-center px-4">
                                          {evaluationsFilter === 'assessment' ? (
                                            <AcademicCapIcon className="h-8 w-8 text-gray-300 mb-2" />
                                          ) : (
                                            <VideoCameraIcon className="h-8 w-8 text-gray-300 mb-2" />
                                          )}
                                          <p className="text-xs font-semibold text-gray-500">
                                            {evaluationsFilter === 'interview' && 'No AI interviews yet'}
                                            {evaluationsFilter === 'assessment' && 'No assessments yet'}
                                            {evaluationsFilter === 'all' && 'No evaluations yet'}
                                          </p>
                                          <p className="text-[10px] text-gray-400 mt-1 max-w-[220px]">
                                            {evaluationsFilter === 'interview' && 'Conduct AI interviews to see results here.'}
                                            {evaluationsFilter === 'assessment' && 'Send assessment invites to see results here.'}
                                            {evaluationsFilter === 'all' && 'Conduct AI interviews or send assessment invites to see results here.'}
                                          </p>
                                        </div>
                                      ) : filteredEvaluations.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 flex-grow text-center px-4">
                                          <FunnelIcon className="h-8 w-8 text-gray-300 mb-2" />
                                          <p className="text-xs font-semibold text-gray-500">No matching evaluations</p>
                                          <p className="text-[10px] text-gray-400 mt-1 max-w-[220px]">
                                            Try changing your filter options to see other evaluations.
                                          </p>
                                        </div>
                                      ) : (
                                        <div className="divide-y divide-gray-50 overflow-y-auto flex-grow scrollbar-thin-light">
                                          {filteredEvaluations.map((item, idx) => {
                                            const isInterview = item.type === 'interview';
                                            const stCfg = statusConfig[item.status] || statusConfig.pending;
                                            const breadcrumb = isInterview
                                              ? [item.jobTitle, item.screeningName || item.interviewTitle].filter(Boolean).join(' › ')
                                              : [item.jobTitle, item.screeningName || item.assessmentTitle].filter(Boolean).join(' › ');

                                            return (
                                              <motion.div
                                                key={`${item.type}-${item.sessionId || item.invitedCandidateId}-${idx}`}
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                                                onClick={() => handleEvalItemClick(item)}
                                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-150 group ${item.charged === false
                                                  ? 'bg-red-50/60 hover:bg-red-50'
                                                  : 'hover:bg-gray-50/80'
                                                  }`}
                                              >
                                                {/* Type Icon */}
                                                <div className={`shrink-0 rounded-xl p-2 transition-colors ${isInterview
                                                  ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100'
                                                  : 'bg-amber-50 text-amber-600 group-hover:bg-amber-100'
                                                  }`}>
                                                  {isInterview ? (
                                                    <VideoCameraIcon className="h-4 w-4" />
                                                  ) : (
                                                    <AcademicCapIcon className="h-4 w-4" />
                                                  )}
                                                </div>

                                                {/* Details */}
                                                <div className="min-w-0 flex-1">
                                                  <div className="flex items-center gap-2">
                                                    <p className="text-xs font-bold text-gray-900 truncate">
                                                      {item.jobId ? (
                                                        <a
                                                          href={item.screeningId
                                                            ? `/dashboard/job-posts/jobs/${item.jobId}/screenings/${item.screeningId}`
                                                            : `/dashboard/job-posts/jobs/${item.jobId}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          onClick={(e) => e.stopPropagation()}
                                                          className="hover:text-indigo-700 hover:underline transition duration-150 cursor-pointer"
                                                          title={`View screening details for ${item.candidateName}`}
                                                        >
                                                          {item.candidateName}
                                                        </a>
                                                      ) : (
                                                        item.candidateName
                                                      )}
                                                    </p>
                                                    <span className={`shrink-0 inline-flex items-center rounded-full px-1.5 py-0.5 text-[8px] font-bold border ${stCfg.bg} ${stCfg.text} ${stCfg.border}`}>
                                                      {stCfg.label}
                                                    </span>
                                                    {item.charged === false && (
                                                      <span
                                                        className="shrink-0 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-bold border bg-red-50 text-red-600 border-red-100 cursor-help"
                                                        data-tooltip-id="free-interviews-tooltip"
                                                        data-tooltip-content="Candidate left the interview early — not charged."
                                                      >
                                                        Not charged
                                                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5 shrink-0 text-red-500/80">
                                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                                                        </svg>
                                                      </span>
                                                    )}
                                                  </div>
                                                  {breadcrumb && (
                                                    <p
                                                      className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[280px]"
                                                      data-tooltip-id="evals-tooltip"
                                                      data-tooltip-content={breadcrumb}
                                                    >
                                                      {breadcrumb}
                                                    </p>
                                                  )}
                                                </div>

                                                {/* Score + Date */}
                                                <div className="shrink-0 flex items-center gap-2.5">
                                                  {item.score != null && (
                                                    <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-extrabold ${item.score >= 70
                                                      ? 'bg-emerald-50 text-emerald-700'
                                                      : item.score >= 40
                                                        ? 'bg-amber-50 text-amber-700'
                                                        : 'bg-red-50 text-red-600'
                                                      }`}>
                                                      <span>{item.score}%</span>
                                                    </div>
                                                  )}
                                                  <div className="text-right">
                                                    <p className="text-[9px] text-gray-400 whitespace-nowrap">{formatEvalDate(item.date)}</p>
                                                  </div>
                                                  <EyeIcon className="h-3.5 w-3.5 text-gray-300 group-hover:text-indigo-500 transition-colors shrink-0" />
                                                </div>
                                              </motion.div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {selectedCard === 'sourcing' && (
                              <div className="grid gap-8 lg:grid-cols-[1.2fr_1.2fr]">
                                {/* Left: Sourcing & Screening Trend & Stats */}
                                <div className="space-y-6">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-base font-bold text-gray-900 mb-0.5">Resume Screening Trend</h3>
                                      <p className="text-xs text-gray-500">Daily screening/job posting counts</p>
                                    </div>
                                  </div>

                                  <div className="rounded-2xl border border-gray-100 p-4 bg-white shadow-sm min-h-[220px]">
                                    {chartLoading ? (
                                      <div className="flex items-center justify-center h-48 text-gray-400 text-xs">
                                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-[3px] border-gray-200 border-t-[#f97316]" />
                                      </div>
                                    ) : chartData.length === 0 ? (
                                      <div className="flex items-center justify-center h-48 text-gray-400 text-xs">No activity trend available.</div>
                                    ) : (
                                      <ResponsiveContainer width="100%" height={180}>
                                        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                                          <XAxis dataKey="date" tickFormatter={chartDateFormatter} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                          <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,.04)' }} labelFormatter={(l) => chartDateFormatter(l)} />
                                          <Line type="monotone" dataKey="screenings" name="Screenings" stroke="#8b5cf6" strokeWidth={2.5} dot={false} />
                                          <Line type="monotone" dataKey="jobPosts" name="Job Posts" stroke="#3b82f6" strokeWidth={2.5} dot={false} />
                                        </LineChart>
                                      </ResponsiveContainer>
                                    )}
                                  </div>

                                  {/* Headline: total resumes screened */}
                                  <div className="rounded-2xl border border-gray-100 bg-[#F5F3FF] p-4 flex items-center gap-3 shadow-sm">
                                    <div className="rounded-xl bg-[#8b5cf6]/10 p-2.5 text-[#8b5cf6]">
                                      <DocumentTextIcon className="h-5 w-5" />
                                    </div>
                                    <div>
                                      <p className="text-xl font-black text-gray-900 leading-tight">
                                        {(usage?.screenedResumes ?? 0).toLocaleString()}
                                      </p>
                                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">Resumes Screened</p>
                                    </div>
                                  </div>

                                  {/* Where the resumes came from (channel breakdown) */}
                                  {sourcingBreakdown.length > 0 && (() => {
                                    const channelTotal = sourcingBreakdown.reduce((s, b) => s + b.count, 0);
                                    const channelColors = {
                                      'Job Application': '#8b5cf6',
                                      'Resume Upload': '#16a34a',
                                      'AI Sourcing': '#f97316',
                                      'Manual Entry': '#3b82f6',
                                      'Other': '#9ca3af',
                                    };
                                    return (
                                      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-3">
                                        <div className="flex items-center justify-between">
                                          <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Resumes by Channel</p>
                                          <span className="text-[10px] text-gray-400 font-semibold">{channelTotal.toLocaleString()} total</span>
                                        </div>
                                        <div className="space-y-2.5">
                                          {sourcingBreakdown.map((b) => {
                                            const color = channelColors[b.channel] || '#9ca3af';
                                            const pct = channelTotal > 0 ? Math.round((b.count / channelTotal) * 100) : 0;
                                            return (
                                              <div key={b.channel}>
                                                <div className="flex items-center justify-between text-[11px] mb-1">
                                                  <span className="font-semibold text-gray-700 flex items-center gap-1.5">
                                                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                                                    {b.channel}
                                                  </span>
                                                  <span className="font-bold text-gray-900">{b.count.toLocaleString()} <span className="text-gray-400 font-medium">({pct}%)</span></span>
                                                </div>
                                                <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                                                  <motion.div
                                                    className="h-full rounded-full"
                                                    style={{ backgroundColor: color }}
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.6, ease: 'easeOut' }}
                                                  />
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Right: Screened Candidates Feed */}
                                <div className="flex flex-col bg-white border border-gray-100 rounded-3xl p-6 shadow-sm max-h-[460px] min-h-[460px]">
                                  <div className="flex items-center justify-between mb-4 shrink-0">
                                    <div>
                                      <h3 className="text-base font-bold text-gray-900">Screened Resumes</h3>
                                      <p className="text-xs text-gray-500">Recent candidates parsed & scored</p>
                                    </div>
                                    {sourcingTotal > 0 && (
                                      <span className="inline-flex items-center rounded-full bg-[#FFEFE6] px-2.5 py-0.5 text-xs font-semibold text-[#f97316] border border-[#ffd2b3]">
                                        {sourcingTotal.toLocaleString()} screened
                                      </span>
                                    )}
                                  </div>

                                  {sourcingListLoading ? (
                                    <div className="flex flex-col items-center justify-center flex-grow py-10">
                                      <span className="h-6 w-6 border-2 border-[#f97316] border-t-transparent rounded-full animate-spin mb-2" />
                                      <p className="text-xs text-gray-400 font-medium">Loading screened resumes…</p>
                                    </div>
                                  ) : sourcingList.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center border border-dashed border-gray-100 rounded-2xl p-8 bg-gray-50/50 flex-grow">
                                      <DocumentTextIcon className="h-8 w-8 text-gray-300 mb-2" />
                                      <p className="text-xs font-semibold text-gray-500">No screened resumes yet</p>
                                      <p className="text-[10px] text-gray-400 mt-0.5">Candidates parsed will appear here.</p>
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-gray-50 overflow-y-auto flex-grow scrollbar-thin-light pr-1.5 -mr-1.5">
                                      {sourcingList.map((item, idx) => {
                                        const breadcrumb = [item.jobTitle, item.screeningName].filter(Boolean).join(' › ');
                                        return (
                                          <motion.div
                                            key={`sourcing-${item.candidateId}-${idx}`}
                                            initial={{ opacity: 0, x: 10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                                            className="flex items-center gap-3 px-1 py-3 hover:bg-gray-50/50 rounded-xl transition-all duration-150 group"
                                          >
                                            {/* Avatar Icon */}
                                            <div className="shrink-0 rounded-xl bg-[#FFEFE6] text-[#f97316] p-2">
                                              <DocumentTextIcon className="h-4 w-4" />
                                            </div>

                                            {/* Details */}
                                            <div className="min-w-0 flex-1">
                                              <div className="flex items-center gap-2">
                                                <p className="text-xs font-bold text-gray-900 truncate">
                                                  {item.jobId ? (
                                                    <a
                                                      href={item.screeningId
                                                        ? `/dashboard/job-posts/jobs/${item.jobId}/screenings/${item.screeningId}`
                                                        : `/dashboard/job-posts/jobs/${item.jobId}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="hover:text-indigo-700 hover:underline transition duration-150 cursor-pointer"
                                                      title={`View screening details for ${item.candidateName}`}
                                                    >
                                                      {item.candidateName}
                                                    </a>
                                                  ) : (
                                                    item.candidateName
                                                  )}
                                                </p>
                                                {item.score != null && (
                                                  <div className={`shrink-0 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold ${item.score >= 70
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : item.score >= 40
                                                      ? 'bg-amber-50 text-amber-700'
                                                      : 'bg-red-50 text-red-600'
                                                    }`}>
                                                    <span>{item.score}%</span>
                                                  </div>
                                                )}
                                              </div>
                                              {breadcrumb && (
                                                <p
                                                  className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[280px]"
                                                  data-tooltip-id="evals-tooltip"
                                                  data-tooltip-content={breadcrumb}
                                                >
                                                  {breadcrumb}
                                                </p>
                                              )}
                                            </div>

                                            {/* View Action */}
                                            <div className="shrink-0 flex items-center gap-2">
                                              <div className="text-right hidden sm:block">
                                                <p className="text-[9px] text-gray-400 whitespace-nowrap">{formatEvalDate(item.date)}</p>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => openCandidateResumeBreakdown(item)}
                                                className="px-2.5 py-1 text-[10px] font-bold text-[#f97316] bg-[#FFEFE6] hover:bg-[#ffd2b3]/50 rounded-lg transition-all"
                                              >
                                                View
                                              </button>
                                            </div>
                                          </motion.div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {selectedCard === 'funnel' && (
                              <div className="grid gap-8 lg:grid-cols-[1.1fr_1.3fr]">
                                {/* Left: Visual Funnel */}
                                <div className="space-y-4">
                                  <div className="flex items-center gap-2.5">
                                    <div className="rounded-xl bg-[#E8F4FF] p-2 text-[#3b82f6]">
                                      <UsersIcon className="h-5 w-5" />
                                    </div>
                                    <div>
                                      <h3 className="text-base font-bold text-gray-900">Visual Recruitment Funnel</h3>
                                      <p className="text-xs text-gray-500">A graphical representation of candidate progress</p>
                                    </div>
                                  </div>

                                  <div className="space-y-3 bg-gray-50/50 rounded-2xl border border-gray-100 p-5">
                                    {/* 1. Total Candidates */}
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs font-semibold text-gray-700">
                                        <span>1. Total Candidates Invite List</span>
                                        <span>{candidatesTotal} (100%)</span>
                                      </div>
                                      <div className="h-6 w-full rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm">
                                        Funnel Entrance
                                      </div>
                                    </div>

                                    {/* 2. Resumes Screened */}
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs font-semibold text-gray-700">
                                        <span>2. Screened & Rated</span>
                                        <span>{(usage?.screenings ?? 0)} ({candidatesTotal > 0 ? Math.round(((usage?.screenings ?? 0) / candidatesTotal) * 100) : 0}%)</span>
                                      </div>
                                      <div className="h-6 rounded-lg bg-gradient-to-r from-sky-400 to-blue-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm" style={{ width: '90%', margin: '0 auto' }}>
                                        Resume Scoring
                                      </div>
                                    </div>

                                    {/* 3. Evaluated Candidates */}
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs font-semibold text-gray-700">
                                        <span>3. AI Interview & Test Completed</span>
                                        <span>{evaluationsTotal} ({candidatesTotal > 0 ? Math.round((evaluationsTotal / candidatesTotal) * 100) : 0}%)</span>
                                      </div>
                                      <div className="h-6 rounded-lg bg-gradient-to-r from-emerald-400 to-teal-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm" style={{ width: '75%', margin: '0 auto' }}>
                                        Interviews & Custom Tests
                                      </div>
                                    </div>

                                    {/* 4. Shortlisted Candidates */}
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs font-semibold text-gray-700">
                                        <span>4. Selected for Hiring Manager</span>
                                        <span>{shortlistedTotal} ({shortlistRate}%)</span>
                                      </div>
                                      <div className="h-6 rounded-lg bg-gradient-to-r from-indigo-400 to-purple-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm" style={{ width: '55%', margin: '0 auto' }}>
                                        Selected
                                      </div>
                                    </div>

                                    {/* 5. Final Placements / Offered */}
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs font-semibold text-gray-700">
                                        <span>5. Final Job Offers Released</span>
                                        <span>{offeredTotal} ({candidatesTotal > 0 ? ((offeredTotal / candidatesTotal) * 100).toFixed(1) : "0.0"}%)</span>
                                      </div>
                                      <div className="h-6 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm" style={{ width: '35%', margin: '0 auto' }}>
                                        Offered
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Pipeline Candidates */}
                                <div className="flex flex-col bg-white border border-gray-100 rounded-3xl p-6 shadow-sm max-h-[460px] min-h-[460px]">
                                  <div className="flex items-center justify-between mb-4 shrink-0">
                                    <div>
                                      <h3 className="text-base font-bold text-gray-900">Pipeline Candidates</h3>
                                      <p className="text-xs text-gray-500 font-sans">Candidates at each stage of the funnel</p>
                                    </div>
                                  </div>

                                  {/* Horizontal 3 Tabs */}
                                  <div className="flex border-b border-gray-100 mb-4 overflow-x-auto scrollbar-none shrink-0 gap-1 pr-1">
                                    {[
                                      { id: 'all', label: 'All Candidates', count: (pipelineLists.all || []).length },
                                      { id: 'shortlisted', label: 'Selected', count: (pipelineLists.shortlisted || []).length },
                                      { id: 'rejected', label: 'Rejected', count: (pipelineLists.rejected || []).length },
                                    ].map((tab) => {
                                      const isActive = pipelineActiveTab === tab.id;
                                      return (
                                        <button
                                          key={tab.id}
                                          type="button"
                                          onClick={() => setPipelineActiveTab(tab.id)}
                                          className={`pb-2 px-2.5 text-xs font-bold transition-all relative border-b-2 whitespace-nowrap flex items-center gap-1 ${isActive
                                            ? 'text-[#f97316] border-[#f97316]'
                                            : 'text-gray-400 border-transparent hover:text-gray-600'
                                            }`}
                                        >
                                          <span>{tab.label}</span>
                                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-extrabold ${isActive
                                            ? 'bg-[#FFEFE6] text-[#f97316]'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {tab.count}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {pipelineLoading ? (
                                    <div className="flex flex-col items-center justify-center flex-grow py-10">
                                      <span className="h-6 w-6 border-2 border-[#f97316] border-t-transparent rounded-full animate-spin mb-2" />
                                      <p className="text-xs text-gray-400 font-medium">Loading pipeline candidates…</p>
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-gray-50 overflow-y-auto flex-grow scrollbar-thin-light pr-1.5 -mr-1.5">
                                      {(() => {
                                        const activeList = pipelineLists[pipelineActiveTab] || [];
                                        if (activeList.length === 0) {
                                          return (
                                            <div className="flex flex-col items-center justify-center border border-dashed border-gray-100 rounded-2xl p-8 bg-gray-50/50 flex-grow h-[300px]">
                                              <UsersIcon className="h-8 w-8 text-gray-300 mb-2" />
                                              <p className="text-xs font-semibold text-gray-500">No candidates in this stage</p>
                                            </div>
                                          );
                                        }
                                        return activeList.map((item, idx) => {
                                          const breadcrumb = [item.jobTitle, item.screeningName].filter(Boolean).join(' › ');
                                          return (
                                            <motion.div
                                              key={`pipeline-${item.candidateId}-${idx}`}
                                              initial={{ opacity: 0, x: 10 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                                              className="flex items-center gap-3 px-1 py-3 hover:bg-gray-50/50 rounded-xl transition-all duration-150 group"
                                            >
                                              {/* Avatar Icon */}
                                              <div className="shrink-0 rounded-xl bg-[#FFEFE6] text-[#f97316] p-2">
                                                <UserCircleIcon className="h-4 w-4" />
                                              </div>

                                              {/* Details */}
                                              <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                  <p className="text-xs font-bold text-gray-900 truncate">
                                                    {item.jobId ? (
                                                      <a
                                                        href={item.screeningId
                                                          ? `/dashboard/job-posts/jobs/${item.jobId}/screenings/${item.screeningId}`
                                                          : `/dashboard/job-posts/jobs/${item.jobId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-indigo-700 hover:underline transition duration-150 cursor-pointer"
                                                        title={`View screening details for ${item.candidateName}`}
                                                      >
                                                        {item.candidateName}
                                                      </a>
                                                    ) : (
                                                      item.candidateName
                                                    )}
                                                  </p>
                                                  <div className="flex items-center gap-1 shrink-0">
                                                    {item.score != null && (
                                                      <span className="text-[8px] font-extrabold bg-[#FFEFE6] text-[#f97316] px-1 py-0.5 rounded" title="Resume Match Score">
                                                        R: {item.score}%
                                                      </span>
                                                    )}
                                                    {item.assessmentScore != null && (
                                                      <span className="text-[8px] font-extrabold bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded" title="Assessment Score">
                                                        T: {item.assessmentScore}%
                                                      </span>
                                                    )}
                                                    {item.interviewScore != null && (
                                                      <span className="text-[8px] font-extrabold bg-purple-50 text-purple-700 px-1 py-0.5 rounded" title="AI Interview Score">
                                                        I: {item.interviewScore}%
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                {breadcrumb && (
                                                  <p className="text-[9px] text-gray-400 mt-0.5 truncate max-w-[220px]">
                                                    {breadcrumb}
                                                  </p>
                                                )}
                                              </div>

                                              {/* Action View */}
                                              <div className="shrink-0 flex items-center gap-2">
                                                <div className="text-right hidden sm:block">
                                                  <p className="text-[9px] text-gray-400 whitespace-nowrap">{formatEvalDate(item.date)}</p>
                                                </div>
                                                {(() => {
                                                  const hasInterview = item.interviewSessionId != null || item.interviewScore != null;
                                                  const hasAssessment = item.assessmentId != null || item.assessmentScore != null;
                                                  if (hasInterview) {
                                                    return (
                                                      <button
                                                        type="button"
                                                        onClick={() => openInterviewSessionForCandidate(item)}
                                                        className="px-2.5 py-1 text-[10px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all"
                                                      >
                                                        Replay
                                                      </button>
                                                    );
                                                  } else if (hasAssessment) {
                                                    return (
                                                      <button
                                                        type="button"
                                                        onClick={() => handleInlineViewAssessmentScore(item)}
                                                        className="px-2.5 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
                                                      >
                                                        Score
                                                      </button>
                                                    );
                                                  } else {
                                                    return (
                                                      <button
                                                        type="button"
                                                        onClick={() => openCandidateResumeBreakdown(item)}
                                                        className="px-2.5 py-1 text-[10px] font-bold text-[#f97316] bg-[#FFEFE6] hover:bg-[#ffd2b3]/50 rounded-lg transition-all"
                                                      >
                                                        View
                                                      </button>
                                                    );
                                                  }
                                                })()}
                                              </div>
                                            </motion.div>
                                          );
                                        });
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      </div>,
                      document.body
                    )}

                    {/* ─── Activity Section: Heatmap + Activity Feed ─── */}
                    <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">

                      {/* ─── LEFT: GitHub-style Activity Heatmap Calendar ─── */}
                      <div className="xl:col-span-3 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-[#242124] flex items-center gap-2">
                            <ChartBarIcon className="h-4 w-4 text-[#007BFF]" />
                            Activity Calendar
                          </h3>
                          {!heatmapLoading && (
                            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                              {heatmapTotalActivity.toLocaleString()} activities this year
                            </span>
                          )}
                        </div>

                        {heatmapLoading ? (
                          <div className="flex flex-col items-center justify-center py-14 text-gray-500 text-sm">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-[3px] border-gray-200 border-t-[#007BFF]" />
                            <p className="mt-2 text-xs">Loading activity calendar…</p>
                          </div>
                        ) : (
                          <>
                            {/* Heatmap Grid */}
                            {(() => {
                              // Build weeks matrix (columns = weeks, rows = days 0=Sun..6=Sat)
                              const data = heatmapData;
                              if (!data || data.length === 0) return (
                                <div className="flex flex-col items-center justify-center py-14 text-gray-400">
                                  <ChartBarIcon className="h-10 w-10 mb-2 text-gray-300" />
                                  <p className="text-sm font-medium text-gray-500">No activity data yet</p>
                                </div>
                              );

                              // Build weeks from data
                              const weeks = [];
                              let currentWeek = new Array(7).fill(null);
                              data.forEach((day, i) => {
                                const d = new Date(day.date + 'T00:00:00');
                                const dow = d.getDay(); // 0=Sun
                                if (i === 0) {
                                  // Fill leading nulls for partial first week
                                  currentWeek = new Array(7).fill(null);
                                }
                                if (dow === 0 && i > 0) {
                                  weeks.push(currentWeek);
                                  currentWeek = new Array(7).fill(null);
                                }
                                currentWeek[dow] = day;
                              });
                              weeks.push(currentWeek); // push last week

                              // Color intensity function
                              const getColor = (count) => {
                                if (count === 0) return 'bg-gray-100';
                                if (count <= 2) return 'bg-emerald-200';
                                if (count <= 5) return 'bg-emerald-300';
                                if (count <= 10) return 'bg-emerald-400';
                                if (count <= 20) return 'bg-emerald-500';
                                return 'bg-emerald-600';
                              };

                              // Month labels
                              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const monthLabels = [];
                              let lastMonth = -1;
                              weeks.forEach((week, wi) => {
                                // Find first non-null day in this week
                                const firstDay = week.find(d => d !== null);
                                if (firstDay) {
                                  const m = new Date(firstDay.date + 'T00:00:00').getMonth();
                                  if (m !== lastMonth) {
                                    monthLabels.push({ weekIdx: wi, label: monthNames[m] });
                                    lastMonth = m;
                                  }
                                }
                              });

                              const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
                              const cellSize = 12;
                              const cellGap = 3;

                              return (
                                <div className="overflow-x-auto">
                                  <div className="relative" style={{ paddingLeft: '28px' }}>
                                    {/* Month labels - positioned above the grid */}
                                    <div className="relative" style={{ height: '18px', marginBottom: '2px' }}>
                                      {monthLabels.map((ml, i) => (
                                        <span
                                          key={i}
                                          className="text-[9px] font-medium text-gray-400 absolute"
                                          style={{
                                            left: `${ml.weekIdx * (cellSize + cellGap)}px`,
                                            top: 0,
                                          }}
                                        >
                                          {ml.label}
                                        </span>
                                      ))}
                                    </div>

                                    {/* Grid area: day labels + week columns */}
                                    <div className="flex">
                                      {/* Day labels column */}
                                      <div className="flex flex-col justify-between shrink-0" style={{ width: '28px', height: `${7 * (cellSize + cellGap) - cellGap}px`, marginLeft: '-28px' }}>
                                        {dayLabels.map((lbl, i) => (
                                          <span key={i} className="text-[9px] font-medium text-gray-400 leading-none" style={{ height: `${cellSize}px`, display: 'flex', alignItems: 'center' }}>
                                            {lbl}
                                          </span>
                                        ))}
                                      </div>

                                      {/* Weeks grid */}
                                      <div className="flex gap-[3px]">
                                        {weeks.map((week, wi) => (
                                          <div key={wi} className="flex flex-col gap-[3px]">
                                            {week.map((day, di) => {
                                              if (!day) return <div key={di} style={{ width: cellSize, height: cellSize }} />;
                                              const d = new Date(day.date + 'T00:00:00');
                                              const dateLabel = `${monthNames[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
                                              const tooltipContent = day.total === 0
                                                ? `No activity on ${dateLabel}`
                                                : `<div style="text-align:left;line-height:1.5"><strong>${day.total} activit${day.total === 1 ? 'y' : 'ies'}</strong> on ${dateLabel}<br/>${day.interviews ? `<span style="color:#10b981">● ${day.interviews} interview${day.interviews > 1 ? 's' : ''}</span><br/>` : ''}${day.assessments ? `<span style="color:#f59e0b">● ${day.assessments} assessment${day.assessments > 1 ? 's' : ''}</span><br/>` : ''}${day.jobPosts ? `<span style="color:#3b82f6">● ${day.jobPosts} job post${day.jobPosts > 1 ? 's' : ''}</span><br/>` : ''}${day.screenings ? `<span style="color:#8b5cf6">● ${day.screenings} screening${day.screenings > 1 ? 's' : ''}</span>` : ''}</div>`;

                                              return (
                                                <div
                                                  key={di}
                                                  data-tooltip-id="heatmap-tooltip"
                                                  data-tooltip-html={tooltipContent}
                                                  className={`rounded-[3px] transition-all duration-150 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 cursor-pointer ${getColor(day.total)}`}
                                                  style={{ width: cellSize, height: cellSize }}
                                                />
                                              );
                                            })}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Legend */}
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
                              <span className="text-[10px] text-gray-400 font-medium">Learn how we count activities</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-gray-400 font-medium">Less</span>
                                {['bg-gray-100', 'bg-emerald-200', 'bg-emerald-300', 'bg-emerald-400', 'bg-emerald-500', 'bg-emerald-600'].map((c, i) => (
                                  <div key={i} className={`w-[10px] h-[10px] rounded-[2px] ${c}`} />
                                ))}
                                <span className="text-[10px] text-gray-400 font-medium">More</span>
                              </div>
                            </div>

                            {/* Activity Breakdown + Highlights */}
                            {heatmapData.length > 0 && (() => {
                              // Compute totals by type from heatmap data
                              const totals = heatmapData.reduce((acc, d) => {
                                acc.interviews += d.interviews || 0;
                                acc.assessments += d.assessments || 0;
                                acc.jobPosts += d.jobPosts || 0;
                                acc.screenings += d.screenings || 0;
                                return acc;
                              }, { interviews: 0, assessments: 0, jobPosts: 0, screenings: 0 });

                              // Compute streaks
                              let currentStreak = 0;
                              let longestStreak = 0;
                              let tempStreak = 0;
                              let mostActiveDay = { date: '', count: 0 };
                              const monthTotals = {};

                              heatmapData.forEach((d) => {
                                if (d.total > 0) {
                                  tempStreak += 1;
                                  if (tempStreak > longestStreak) longestStreak = tempStreak;
                                } else {
                                  tempStreak = 0;
                                }
                                if (d.total > mostActiveDay.count) {
                                  mostActiveDay = { date: d.date, count: d.total };
                                }
                                const monthKey = d.date.substring(0, 7); // YYYY-MM
                                monthTotals[monthKey] = (monthTotals[monthKey] || 0) + d.total;
                              });

                              // Current streak = count from last day backwards
                              currentStreak = 0;
                              for (let i = heatmapData.length - 1; i >= 0; i--) {
                                if (heatmapData[i].total > 0) currentStreak++;
                                else break;
                              }

                              // Busiest month
                              let busiestMonth = { key: '', count: 0 };
                              Object.entries(monthTotals).forEach(([key, val]) => {
                                if (val > busiestMonth.count) busiestMonth = { key, count: val };
                              });
                              const busiestMonthLabel = busiestMonth.key
                                ? (() => { const [y, m] = busiestMonth.key.split('-'); return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(m) - 1]} ${y}`; })()
                                : '—';

                              const mostActiveDateLabel = mostActiveDay.date
                                ? (() => { const dt = new Date(mostActiveDay.date + 'T00:00:00'); return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()]} ${dt.getDate()}`; })()
                                : '—';

                              const breakdownItems = [
                                { label: 'Interviews', count: totals.interviews, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: MicrophoneIcon },
                                { label: 'Assessments', count: totals.assessments, color: 'text-amber-600', bg: 'bg-amber-50', icon: AcademicCapIcon },
                                { label: 'Job Posts', count: totals.jobPosts, color: 'text-blue-600', bg: 'bg-blue-50', icon: BriefcaseIcon },
                                { label: 'Screenings', count: totals.screenings, color: 'text-violet-600', bg: 'bg-violet-50', icon: FunnelIcon },
                              ];

                              return (
                                <>
                                  {/* Type Breakdown Row */}
                                  <div className="grid grid-cols-4 gap-2.5 mt-4">
                                    {breakdownItems.map((item) => {
                                      const ItemIcon = item.icon;
                                      return (
                                        <div key={item.label} className={`rounded-xl ${item.bg} border border-transparent px-3 py-2.5 flex items-center gap-2.5 transition-all hover:shadow-sm`}>
                                          <div className={`rounded-lg p-1.5 bg-white/70 shadow-sm`}>
                                            <ItemIcon className={`h-3.5 w-3.5 ${item.color}`} />
                                          </div>
                                          <div>
                                            <p className={`text-base font-bold ${item.color} leading-none`}>{item.count.toLocaleString()}</p>
                                            <p className="text-[9px] font-medium text-gray-500 mt-0.5">{item.label}</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Streak Highlights Row */}
                                  <div className="grid grid-cols-4 gap-2.5 mt-2.5">
                                    {[
                                      {
                                        label: 'Average Interview Time',
                                        value: peakInterviewHour,
                                        sub: peakInterviewHour !== '—' ? 'Peak activity hour' : null,
                                        emoji: '🕒'
                                      },
                                      {
                                        label: 'After-Hours %',
                                        value: peakInterviewHour !== '—' ? `${afterOfficeHoursPercent}%` : '—',
                                        sub: 'Outside 10 AM - 6 PM',
                                        emoji: '🌙'
                                      },
                                      { label: 'Most Active Day', value: mostActiveDateLabel, sub: `${mostActiveDay.count} activities`, emoji: '⚡' },
                                      { label: 'Busiest Month', value: busiestMonthLabel, sub: `${busiestMonth.count} activities`, emoji: '📅' },
                                    ].map((stat) => (
                                      <div key={stat.label} className="rounded-xl bg-gray-50 px-3 py-2.5 flex items-start gap-2 transition-all hover:bg-gray-100/70">
                                        <span className="text-sm mt-0.5">{stat.emoji}</span>
                                        <div>
                                          <p className="text-sm font-bold text-[#242124] leading-none">{stat.value}</p>
                                          <p className="text-[9px] font-medium text-gray-400 mt-0.5">{stat.label}</p>
                                          {stat.sub && <p className="text-[8px] text-gray-400">{stat.sub}</p>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </div>

                      {/* ─── RIGHT: Recent Activity Feed ─── */}
                      <div className="xl:col-span-2 rounded-xl border border-gray-100 bg-white p-5 shadow-sm flex flex-col" style={{ maxHeight: '440px' }}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-[#242124] flex items-center gap-2">
                            <ClockIcon className="h-4 w-4 text-[#007BFF]" />
                            Recent Activity
                          </h3>
                          {!activityLoading && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                              {activityTotal.toLocaleString()}
                            </span>
                          )}
                        </div>

                        {/* Filter pills */}
                        <div className="flex items-center gap-1 mb-3 flex-wrap">
                          {filterOptions.map((f) => (
                            <button
                              key={f.key}
                              type="button"
                              onClick={() => { setActivityFilter(f.key); setActivityPage(1); }}
                              className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${activityFilter === f.key
                                ? 'bg-[#007BFF] text-white shadow-sm'
                                : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                }`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>

                        {/* Activity List */}
                        <div className="flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}>
                          {activityLoading ? (
                            <div className="flex flex-col items-center justify-center py-10 text-gray-500 text-sm">
                              <div className="inline-block animate-spin rounded-full h-6 w-6 border-[3px] border-gray-200 border-t-[#007BFF]" />
                              <p className="mt-2 text-xs">Loading activity…</p>
                            </div>
                          ) : recentActivity.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                              <ClockIcon className="h-8 w-8 mb-2 text-gray-300" />
                              <p className="text-xs font-medium text-gray-500">No activity yet</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                Start using TalentCrux to see activity here.
                              </p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="absolute left-[15px] top-1 bottom-1 w-px bg-gradient-to-b from-gray-200 via-gray-100 to-transparent" />
                              <div className="space-y-0.5">
                                {recentActivity.map((ev, idx) => {
                                  const cfg = activityTypeConfig[ev.type] || activityTypeConfig.assessment;
                                  const EvIcon = cfg.icon;

                                  // Build a descriptive sentence (8-10 words) based on event type
                                  let description = '';
                                  if (ev.type === 'interview') {
                                    if (ev.candidate && ev.status === 'completed') {
                                      description = `${ev.candidate} completed "${ev.title}" AI interview${ev.score != null ? ` scoring ${ev.score}%` : ''}`;
                                    } else if (ev.candidate && ev.status === 'in_progress') {
                                      description = `${ev.candidate} is currently taking "${ev.title}" AI interview`;
                                    } else if (ev.candidate && ev.status === 'invited') {
                                      description = `${ev.candidate} was invited for "${ev.title}" AI interview`;
                                    } else if (ev.candidate && (ev.status === 'abandoned' || ev.status === 'expired')) {
                                      description = `${ev.candidate} ${ev.status} the "${ev.title}" AI interview session`;
                                    } else {
                                      description = `${ev.candidate || 'A candidate'} participated in "${ev.title}" AI interview`;
                                    }
                                  } else if (ev.type === 'assessment') {
                                    if (ev.candidate && ev.status === 'completed') {
                                      description = `${ev.candidate} completed "${ev.title}" assessment${ev.score != null ? ` with ${ev.score}% score` : ''}`;
                                    } else if (ev.candidate && ev.status === 'in_progress') {
                                      description = `${ev.candidate} started taking "${ev.title}" assessment test`;
                                    } else if (ev.candidate && ev.status === 'invited') {
                                      description = `${ev.candidate} was invited for "${ev.title}" assessment`;
                                    } else {
                                      description = `${ev.candidate || 'A candidate'} attempted "${ev.title}" assessment`;
                                    }
                                  } else if (ev.type === 'job_post') {
                                    if (ev.status === 'active') {
                                      description = `New job post created and published — "${ev.title}"`;
                                    } else if (ev.status === 'draft') {
                                      description = `Job post "${ev.title}" was saved as draft`;
                                    } else if (ev.status === 'closed') {
                                      description = `Job post "${ev.title}" was closed and archived`;
                                    } else {
                                      description = `Job post "${ev.title}" was created for hiring`;
                                    }
                                  } else if (ev.type === 'screening') {
                                    description = `Resume screening "${ev.title}" was created for candidate evaluation`;
                                  } else {
                                    description = ev.title;
                                  }

                                  return (
                                    <div key={`${ev.type}-${idx}`} className="relative flex items-start gap-2.5 py-2 pl-0.5 group hover:bg-gray-50/50 rounded-lg transition-colors">
                                      <div className={`relative z-10 rounded-lg p-1 ${cfg.bg} ring-2 ring-white shrink-0 mt-0.5`}>
                                        <EvIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-1.5">
                                          <p className="text-[11.5px] font-medium text-[#242124] leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {description}
                                          </p>
                                          <span className="text-[9px] text-gray-400 shrink-0 font-medium mt-0.5">
                                            {formatRelTime(ev.date)}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${cfg.bg} ${cfg.color}`}>
                                            {cfg.label}
                                          </span>
                                          {ev.score != null && (
                                            <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${ev.score >= 70 ? 'bg-emerald-50 text-emerald-700' : ev.score >= 40 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                                              {ev.score}%
                                            </span>
                                          )}
                                          {ev.status && (ev.type === 'interview' || ev.type === 'assessment') && (
                                            <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${ev.status === 'completed' || ev.status === 'passed' ? 'bg-emerald-50 text-emerald-700' :
                                              ev.status === 'in_progress' || ev.status === 'started' ? 'bg-blue-50 text-blue-700' :
                                                ev.status === 'invited' ? 'bg-amber-50 text-amber-700' :
                                                  ev.status === 'abandoned' || ev.status === 'failed' || ev.status === 'expired' ? 'bg-red-50 text-red-600' :
                                                    'bg-gray-50 text-gray-600'
                                              }`}>
                                              {ev.status.replace('_', ' ')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Compact pagination */}
                        {activityTotalPages > 1 && (
                          <div className="flex items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-100">
                            <button
                              type="button"
                              disabled={activityPage <= 1}
                              onClick={() => fetchActivity(activityFilter, activityPage - 1, rangeQuery)}
                              className="rounded-lg px-2 py-1 text-[10px] font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                              ‹ Prev
                            </button>
                            <span className="text-[10px] font-medium text-gray-500 px-2">
                              {activityPage} / {activityTotalPages}
                            </span>
                            <button
                              type="button"
                              disabled={activityPage >= activityTotalPages}
                              onClick={() => fetchActivity(activityFilter, activityPage + 1, rangeQuery)}
                              className="rounded-lg px-2 py-1 text-[10px] font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                              Next ›
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Heatmap Tooltip */}
                    <ReactTooltip
                      id="heatmap-tooltip"
                      place="top"
                      html={true}
                      style={{
                        fontSize: '11px',
                        padding: '8px 12px',
                        borderRadius: '10px',
                        zIndex: 9999,
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(36, 33, 36, 0.92)',
                        maxWidth: '220px',
                        lineHeight: '1.5',
                      }}
                    />
                  </>
                )}

                {/* ══ CONTACT FOOTER ══ */}
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Need more capacity or a plan change?</p>
                    <p className="text-xs text-gray-400 mt-0.5">We'll sort it out within 1 business day.</p>
                  </div>
                  <a href="mailto:support@talentcrux.ai"
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-[#0066cc] transition-colors whitespace-nowrap">
                    Contact us →
                  </a>
                </div>
              </div>
            );
          })()}


        </div>
      </div>
      <ScoreModals />
      {showCreditsModal && (
        <div className="fixed inset-0 bg-[#242124]/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="absolute inset-0" onClick={() => setShowCreditsModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full overflow-hidden p-6 flex flex-col items-center text-center">
            <button
              type="button"
              onClick={() => setShowCreditsModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="w-12 h-12 rounded-full bg-blue-50 text-[#007BFF] flex items-center justify-center mb-4 border border-blue-100">
              <CreditCardIcon className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Buy More Credits</h3>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed max-w-xs">
              To add more AI interview or custom assessment credits to your organization <span className="font-semibold text-gray-800">{orgDisplayName}</span>, please reach out to our team.
            </p>
            <div className="mt-5 w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 flex flex-col items-center">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Direct Email Support</span>
              <span className="text-base font-bold text-[#007BFF] mt-1">support@talentcrux.ai</span>
            </div>
            <div className="mt-6 w-full space-y-2">
              <a
                href={`mailto:support@talentcrux.ai?subject=Purchase More Credits - ${encodeURIComponent(orgDisplayName)}&body=Hi TalentCrux Team,%0A%0AI would like to purchase more credits for our organization (${encodeURIComponent(orgDisplayName)}). Please contact us with pricing and packages.%0A%0AOrganization: ${encodeURIComponent(orgDisplayName)}%0AEmail: ${encodeURIComponent(user?.email || '')}`}
                className="w-full inline-flex justify-center items-center gap-2 rounded-xl bg-[#007BFF] hover:bg-[#0066cc] text-white px-4 py-2.5 text-sm font-semibold shadow-sm hover:shadow transition-all duration-200"
              >
                <EnvelopeIcon className="h-4 w-4" />
                Contact Sales via Email
              </a>
              <button
                type="button"
                onClick={() => setShowCreditsModal(false)}
                className="w-full inline-flex justify-center items-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 text-sm font-medium transition-all duration-200 shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ReactTooltip id="evals-tooltip" place="top" style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '8px', zIndex: 9999 }} />
      <ReactTooltip id="free-interviews-tooltip" place="top" style={{ fontSize: '11px', padding: '6px 10px', borderRadius: '8px', zIndex: 9999, maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }} />
    </div>
  );
};

export default Profile;
